import { useState, useMemo, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Plus, Search, X as XIcon, Loader2, ChevronRight, Clock,
  CheckCircle2, XCircle, Truck, Package2, FileText, Warehouse,
  AlertCircle, ArrowLeft, ArrowRight, Ban, PauseCircle, Eye,
  PackageCheck, Store, Globe, Laptop, Barcode, RotateCcw, CheckSquare, Square, Printer, AlertTriangle,
  Columns, Trash2, Undo2, Wallet, Pencil, Smartphone, MessageCircle} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'
import { enterAbrePrimeiro } from '@/lib/keyboard'
import { pedirExclusao } from '@/lib/confirmarExclusao'
import { useFilial } from '@/lib/filial'
import { ModalFaturamentoPendente } from './FaturamentoPendente'
import EditarVendaModal from '@/components/EditarVendaModal'
import CanalTag from '@/components/CanalTag'
import { useMostrarCanalTag } from '@/lib/useCanalTag'
import { useAuth } from '@/lib/auth'
import { useIsSimplificado } from '@/hooks/useTipoOperacao'
import { useMarketplaceAtivo } from '@/hooks/useModulo'

// =============================================================
// CONSTANTES E HELPERS
// =============================================================
interface Pedido {
  id: number; numero: number; cliente_id: number; status: string;
  origem: string; canal_origem: string; caixa_canal?: string | null; deposito_id: number;
  total: number; valor_st?: number; bike_troca?: number; forma_pagamento: string; parcelas: number;
  criado_em: string; confirmado_em: string | null;
  pagamento_confirmado_em?: string | null; momento_pagamento?: string | null;
  etapas?: { aprovacao?: boolean; separacao?: boolean; embalagem?: boolean; expedicao?: boolean } | null;
  separacao_iniciada_em: string | null; separacao_concluida_em: string | null;
  embalagem_concluida_em: string | null; expedido_em: string | null;
  transportadora: string | null; rastreio_codigo: string | null;
  itens: any[];
  cliente_nome?: string | null;
  cliente_documento?: string | null;
  cliente_uf?: string | null;
  cliente_bairro?: string | null;
  vendedor_id?: number | null;
  vendedor_codigo?: string | null;
  tipo_venda?: string | null;
  rota_id?: number | null;
  rota_nome?: string | null;
  ml_order_id?: string | null;
  mp_payment_id?: string | null;
  coleta_data_limite?: string | null;
  coleta_status?: string | null;
  coleta_substatus?: string | null;
  coleta_modalidade?: string | null;
  coleta_atualizado_em?: string | null;
}

// Valor da venda LÍQUIDO do desconto da bike da troca. BUG 03/07: a lista mostrava
// o total BRUTO (com a bike embutida), inflando o valor — a bike é DESCONTO, não
// receita (vira estoque seminovo). `pedido.total` segue intacto no backend; aqui só
// a EXIBIÇÃO desconta a bike, consistente com cupom/fechamento. bike_troca vem da API.
function valorLiquido(p: { total?: number; bike_troca?: number }): number {
  return Number(p?.total || 0) - Number(p?.bike_troca || 0)
}

// Reimpressão do cupom de uma venda do PDV — pré-visualização de PDF 80mm igual
// à da OS. O backend (GET /pdv/venda/{id}/cupom-pdf) gera o cupom em reportlab
// (emissor da filial ORIGINAL, cliente, itens, bike na troca como desconto,
// formas de pagamento) — sai certo em qualquer impressora. Bloqueia cancelada.
function PreviewCupomModal({ pedidoId, numero, onClose }: { pedidoId: number; numero: number; onClose: () => void }) {
  const [tipo, setTipo] = useState<'cupom' | 'recibo'>('cupom')
  const [url, setUrl] = useState('')
  const [loading, setLoading] = useState(true)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  useEffect(() => {
    let ativo = true
    let criada = ''
    setLoading(true)
    api.get(`/pdv/venda/${pedidoId}/cupom-pdf`, { params: { tipo }, responseType: 'blob' })
      .then(r => {
        if (!ativo) return
        criada = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }))
        setUrl(criada)
      })
      .catch(() => toast.error('Erro ao gerar cupom'))
      .finally(() => { if (ativo) setLoading(false) })
    return () => { ativo = false; if (criada) URL.revokeObjectURL(criada) }
  }, [tipo, pedidoId])

  const imprimir = () => {
    try { iframeRef.current?.contentWindow?.print() }
    catch { if (url) window.open(url, '_blank') }
  }
  const baixar = () => {
    if (!url) return
    const a = document.createElement('a'); a.href = url; a.download = `cupom-${numero}.pdf`; a.click()
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-ink-200">
          <h2 className="font-bold text-ink-900 flex items-center gap-2">
            <Printer className="w-5 h-5 text-violet-600" /> Cupom #{numero}
          </h2>
          <div className="flex items-center gap-1 bg-ink-100 rounded-lg p-0.5">
            {(['cupom', 'recibo'] as const).map(t => (
              <button key={t} onClick={() => setTipo(t)}
                className={`px-3 py-1 rounded-md text-xs font-medium ${tipo === t ? 'bg-white shadow text-ink-900' : 'text-ink-500'}`}>
                {t === 'cupom' ? 'Cupom' : 'Recibo'}
              </button>
            ))}
          </div>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-600"><XIcon className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 bg-ink-100 relative overflow-hidden">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center text-ink-400 gap-2">
              <Loader2 className="w-5 h-5 animate-spin" /> Gerando...
            </div>
          )}
          {url && <iframe ref={iframeRef} src={url} title="Pré-visualização do cupom" className="w-full h-full" />}
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-ink-200">
          <button onClick={baixar} disabled={!url} className="btn-secondary text-sm disabled:opacity-50">Baixar PDF</button>
          <button onClick={() => url && window.open(url, '_blank')} disabled={!url} className="btn-secondary text-sm disabled:opacity-50">Abrir em nova aba</button>
          <button onClick={imprimir} disabled={!url} className="btn-primary text-sm flex items-center gap-1.5 disabled:opacity-50">
            <Printer className="w-4 h-4" /> Imprimir
          </button>
        </div>
      </div>
    </div>
  )
}

// Relatório PDF dos pedidos atualmente filtrados (POST com a lista de ids).
// BUG: o botão "Gerar PDF do relatório" abria o PDF em outra guia (window.open
// '_blank') — o usuário pediu pré-visualização na própria página.
// FIX (26/06): passou a renderizar num modal com <iframe>, igual ao
// PreviewCupomModal, com Baixar / Abrir em nova aba / Imprimir.
function PreviewRelatorioModal({ ids, titulo, onClose }: { ids: number[]; titulo: string; onClose: () => void }) {
  const [url, setUrl] = useState('')
  const [loading, setLoading] = useState(true)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  useEffect(() => {
    let ativo = true
    let criada = ''
    setLoading(true)
    const token = localStorage.getItem('pain_token')
    fetch('/api/vendas/pedidos/relatorio-pdf', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ pedido_ids: ids, titulo }),
    })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob() })
      .then(blob => {
        if (!ativo) return
        criada = URL.createObjectURL(blob)
        setUrl(criada)
      })
      .catch(e => toast.error(`Falha no relatório: ${e.message}`))
      .finally(() => { if (ativo) setLoading(false) })
    return () => { ativo = false; if (criada) URL.revokeObjectURL(criada) }
  }, [ids, titulo])

  const imprimir = () => {
    try { iframeRef.current?.contentWindow?.print() }
    catch { if (url) window.open(url, '_blank') }
  }
  const baixar = () => {
    if (!url) return
    const a = document.createElement('a'); a.href = url; a.download = 'relatorio-pedidos.pdf'; a.click()
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-3" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-4xl h-[92vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-ink-200">
          <h2 className="font-bold text-ink-900 flex items-center gap-2">
            <Printer className="w-5 h-5 text-violet-600" /> {titulo} ({ids.length})
          </h2>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-600"><XIcon className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 bg-ink-100 relative overflow-hidden">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center text-ink-400 gap-2">
              <Loader2 className="w-5 h-5 animate-spin" /> Gerando…
            </div>
          )}
          {url && <iframe ref={iframeRef} src={url} title="Pré-visualização do relatório" className="w-full h-full" />}
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-ink-200">
          <button onClick={baixar} disabled={!url} className="btn-secondary text-sm disabled:opacity-50">Baixar PDF</button>
          <button onClick={() => url && window.open(url, '_blank')} disabled={!url} className="btn-secondary text-sm disabled:opacity-50">Abrir em nova aba</button>
          <button onClick={imprimir} disabled={!url} className="btn-primary text-sm flex items-center gap-1.5 disabled:opacity-50">
            <Printer className="w-4 h-4" /> Imprimir
          </button>
        </div>
      </div>
    </div>
  )
}

// Bucket temporal — espelha lógica real do painel ML:
// hoje   = coleta_status=ready_to_ship (ação do seller hoje) → "Envios de hoje"
// amanha = coleta_status=pending atualizado ontem            → "Coleta amanhã"
// depois = coleta_status=pending atualizado antes            → "A partir de DD/MM"
function bucketMLTempo(p: Pedido): 'hoje' | 'amanha' | 'depois' | null {
  if (p.canal_origem !== 'mercado_livre') return null
  // Cancelado recente entra sempre como alerta de hoje (não enviar)
  if (p.status === 'cancelado') return 'hoje'
  // ready_to_ship (cross OU flex) = ação do seller hoje
  if (p.coleta_status === 'ready_to_ship') return 'hoje'
  // pending/handling = ML ainda processando — divide por idade do shipment
  if (!p.coleta_atualizado_em) return 'depois'
  const upd = new Date(p.coleta_atualizado_em)
  const hojeInicio = new Date()
  hojeInicio.setHours(0, 0, 0, 0)
  const ontemInicio = new Date(hojeInicio)
  ontemInicio.setDate(ontemInicio.getDate() - 1)
  if (upd >= ontemInicio) return 'amanha'
  return 'depois'
}

// Categoria operacional por substatus do shipment (paridade c/ painel ML)
type CatML = 'atrasada' | 'cancelada' | 'nf_pendente' | 'imprimir' | 'pronta' | 'em_proc' | 'flex_imprimir' | 'flex_pronta' | null

function categoriaML(p: Pedido): CatML {
  if (p.canal_origem !== 'mercado_livre') return null
  // Cancelada recente (últimas 96h) — alerta "não enviar"
  if (p.status === 'cancelado') {
    const ts = p.confirmado_em ? new Date(p.confirmado_em).getTime() : new Date(p.criado_em).getTime()
    if (ts >= Date.now() - 96 * 3600 * 1000) return 'cancelada'
    return null
  }
  if (['shipped', 'delivered', 'not_delivered', 'cancelled'].includes(p.coleta_status || '')) return null
  // Atrasada: ready_for_pickup com prazo passado de pelo menos 2 DIAS ÚTEIS
  if (p.coleta_substatus === 'ready_for_pickup' && p.coleta_data_limite) {
    const limite = new Date(p.coleta_data_limite)
    limite.setHours(0, 0, 0, 0)
    let cursor = new Date(limite)
    let diasUteis = 0
    const hoje = new Date()
    hoje.setHours(0, 0, 0, 0)
    while (cursor < hoje) {
      cursor.setDate(cursor.getDate() + 1)
      const dow = cursor.getDay()
      if (dow !== 0 && dow !== 6) diasUteis++
    }
    if (diasUteis >= 2) return 'atrasada'
  }
  // Flex (self_service)
  if (p.coleta_modalidade === 'self_service') {
    if (p.coleta_substatus === 'ready_to_print') return 'flex_imprimir'
    return 'flex_pronta'
  }
  // Cross_docking ready_to_ship — divide em 3 categorias operacionais por substatus
  if (p.coleta_status === 'ready_to_ship') {
    const sub = p.coleta_substatus || ''
    if (sub === 'invoice_pending') return 'nf_pendente'
    if (sub === 'ready_to_print') return 'imprimir'
    return 'pronta' // printed / in_packing_list / ready_for_pickup-não-vencida-2du
  }
  // Cross_docking pending/handling → ML ainda processando
  return 'em_proc'
}

// Visões inteligentes pra operacional saber o que separar
function classificarColeta(p: Pedido): 'flex' | 'hoje' | 'agendado' | 'atrasado' | 'expedido' | null {
  if (p.canal_origem !== 'mercado_livre') return null
  // Já saiu / entregue / cancelada / não-entregue
  if (['shipped', 'delivered', 'not_delivered', 'cancelled'].includes(p.coleta_status || '')) return 'expedido'
  // pending/handling = ML ainda processando (gerando etiqueta), mas vai virar ready_to_ship
  // logo. Painel ML conta esses em "próximos dias" — não filtra como null.
  // FLEX: prioridade máxima — coleta hoje em horas
  if (p.coleta_modalidade === 'self_service') return 'flex'
  // Sem data limite → assume "coletar logo" (hoje)
  if (!p.coleta_data_limite) return 'hoje'
  const limite = new Date(p.coleta_data_limite)
  const agora = new Date()
  const hojeFim = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate(), 23, 59, 59)
  const hojeInicio = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate())
  // Atrasado ignora fim de semana: ML não opera sáb/dom, então um pedido cujo
  // limite caiu sex/sáb/dom só vira atrasado depois do PRÓXIMO dia útil.
  // Além disso, paridade c/ painel ML: pedido só é atrasado se substatus=ready_for_pickup.
  // invoice_pending / printed / outros = aguardando ação interna, não conta como atrasado.
  if (limite < hojeInicio) {
    const tolera = new Date(limite)
    do { tolera.setDate(tolera.getDate() + 1) }
    while (tolera.getDay() === 0 || tolera.getDay() === 6)
    tolera.setHours(23, 59, 59, 999)
    if (agora > tolera && p.coleta_substatus === 'ready_for_pickup') return 'atrasado'
    return 'hoje'
  }
  if (limite <= hojeFim) return 'hoje'
  return 'agendado'
}

function formatDoc(d?: string | null): string {
  if (!d) return ''
  const n = d.replace(/\D/g, '')
  if (n.length === 11) return `${n.slice(0,3)}.${n.slice(3,6)}.${n.slice(6,9)}-${n.slice(9)}`
  if (n.length === 14) return `${n.slice(0,2)}.${n.slice(2,5)}.${n.slice(5,8)}/${n.slice(8,12)}-${n.slice(12)}`
  return d
}

// Colunas opcionais (id estável → label). # e ações ficam sempre.
const COLUNAS_VENDAS = [
  { id: 'cliente',     label: 'Cliente / CPF' },
  { id: 'data',        label: 'Data do pedido' },
  { id: 'tipo_venda',  label: 'Tipo (fiscal/simples)' },
  { id: 'rota',        label: 'Rota' },
  { id: 'uf',          label: 'UF' },
  { id: 'bairro',      label: 'Bairro' },
  { id: 'vendedor',    label: 'Cód. Vendedor' },
  { id: 'canal',       label: 'Canal' },
  { id: 'deposito',    label: 'Depósito' },
  { id: 'total',       label: 'Total' },
  { id: 'status',      label: 'Status' },
  { id: 'tempo',       label: 'Tempo' },
] as const

type ColunaId = (typeof COLUNAS_VENDAS)[number]['id']

function carregarColunasOcultas(): Set<ColunaId> {
  try {
    const raw = localStorage.getItem('pain_vendas_cols_hidden')
    if (!raw) return new Set()
    const arr = JSON.parse(raw) as ColunaId[]
    return new Set(arr)
  } catch { return new Set() }
}

function ColunasMenu({
  ocultas, setOcultas, marketplaceAtivo = true,
}: { ocultas: Set<ColunaId>; setOcultas: (s: Set<ColunaId>) => void; marketplaceAtivo?: boolean }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    if (open) document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  const toggle = (id: ColunaId) => {
    const n = new Set(ocultas)
    if (n.has(id)) n.delete(id); else n.add(id)
    setOcultas(n)
  }
  const colunasVisiveis = COLUNAS_VENDAS
  const total = colunasVisiveis.length
  const visiveis = total - [...ocultas].filter(id => colunasVisiveis.some(c => c.id === id)).length

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(o => !o)}
        className="p-1.5 rounded hover:bg-ink-100 text-ink-500 hover:text-ink-800 transition"
        title="Mostrar/ocultar colunas">
        <Columns size={14} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-ink-200 rounded-lg shadow-xl z-50 overflow-hidden">
          <div className="px-3 py-2 border-b border-ink-100 text-[10px] font-medium text-ink-500 uppercase tracking-wide flex items-center justify-between">
            <span>Colunas visíveis</span>
            <span className="text-ink-400">{visiveis}/{total}</span>
          </div>
          {colunasVisiveis.map(c => {
            const visivel = !ocultas.has(c.id)
            return (
              <label key={c.id}
                className="flex items-center gap-2 px-3 py-2 hover:bg-ink-50 cursor-pointer text-sm">
                <input type="checkbox" checked={visivel} onChange={() => toggle(c.id)}
                  className="w-4 h-4" />
                <span className={visivel ? 'text-ink-900' : 'text-ink-400'}>{c.label}</span>
              </label>
            )
          })}
          {ocultas.size > 0 && (
            <button onClick={() => setOcultas(new Set())}
              className="w-full text-left px-3 py-2 text-xs text-brand-700 hover:bg-brand-50 border-t border-ink-100">
              Mostrar todas
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// === Painel ML — contadores OFICIAIS via API /orders/search do Mercado Livre ===
// Os números aqui batem 100% com o painel do ML (mesma fonte: shipping.substatus filters).
type PainelProps = {
  c: Record<string, number>
  ml: Record<string, number>  // contadores oficiais do endpoint /api/ml/contadores-painel
  filtroStatus: string
  setFiltroStatus: (s: string) => void
}

function MiniLink({ label, qtd, ativo, onClick, danger }: { label: string; qtd: number; ativo: boolean; onClick: () => void; danger?: boolean }) {
  if (!qtd) return null
  return (
    <button onClick={onClick}
      className={`flex items-center justify-between text-[12px] px-1.5 py-1 rounded transition w-full text-left ${
        ativo ? 'bg-brand-100 text-brand-800 font-medium'
              : (danger ? 'text-rose-700 hover:bg-rose-50' : 'text-ink-700 hover:bg-ink-50')
      }`}>
      <span>{label}</span>
      <span className={`tabular-nums ${danger && !ativo ? 'text-rose-600 font-medium' : 'text-ink-500'}`}>{qtd}</span>
    </button>
  )
}

function CardML({ titulo, subtitulo, total, ativo, onClickTotal, children, accent }: {
  titulo: string; subtitulo?: string; total: number; ativo: boolean;
  onClickTotal: () => void; children: React.ReactNode; accent: string
}) {
  return (
    <div className={`rounded-lg border bg-white overflow-hidden transition ${ativo ? 'border-brand-500 ring-2 ring-brand-200' : 'border-ink-200'}`}>
      <button onClick={onClickTotal}
        className={`w-full flex items-start justify-between px-3 py-2 ${accent}`}>
        <div className="text-left">
          <div className="text-[10px] font-semibold uppercase tracking-wide opacity-80">{titulo}</div>
          {subtitulo && <div className="text-sm font-bold leading-tight">{subtitulo}</div>}
        </div>
        <span className="text-[11px] bg-white/70 text-ink-800 rounded-full px-2 py-0.5 font-bold tabular-nums">{total}</span>
      </button>
      <div className="px-2 py-1.5 space-y-0.5 bg-white">
        {children}
      </div>
    </div>
  )
}

function PainelML({ c, ml, filtroStatus, setFiltroStatus }: PainelProps) {
  const [aba, setAba] = useState<'hoje' | 'proximos' | 'finalizados'>('hoje')

  // Contadores OFICIAIS via API ML — substatus exatos
  const atrasada = ml.atrasada || 0
  const cancelada = ml.cancelada_recente || 0
  const nfPendente = ml.invoice_pending || 0
  const imprimir = ml.ready_to_print || 0
  const impressa = ml.printed || 0
  const emPickup = ml.ready_for_pickup || 0
  const emPacking = ml.in_packing_list || 0
  const buffered = ml.buffered || 0
  const manufacturing = ml.manufacturing || 0
  const totFinalizados = c.ml_finalizados || 0

  // Totais oficiais ML
  const readyToShip = ml.ready_to_ship_total || 0   // = "Envios de hoje" do painel ML
  const pending = ml.pending_total || 0             // = "Próximos dias" do painel ML

  // Flex (self_service): API não tem filtro logistic_type → conta do nosso DB sincronizado
  const flexImprimir = c.ml_flex_imprimir || 0
  const flexPronta = c.ml_flex_pronta || 0
  const flexTotal = flexImprimir + flexPronta

  // Cross_docking (Programada Coleta) = ready_to_ship total - flex
  const programadaTotal = readyToShip - flexTotal + cancelada
  const programadaPronta = impressa + emPickup + emPacking // todos prontos pra coleta

  // Próximos dias — pending (ML preparando) tem subdivisões via substatus que se relacionam
  // ao status pending. invoice_pending pode estar em pending tb mas ML conta como hoje.
  // Dividimos amanhã/depois usando coleta_atualizado_em do nosso DB.
  const amanhaTotal = c.ml_amanha_total || 0
  const depoisTotal = c.ml_depois_total || 0
  // Se a API ML diz pending=120 mas nosso DB ainda não atualizou, força total ≈ pending
  const proxTotal = pending

  // Próx data útil pra label do card "A partir de DD/MM"
  const proxData = new Date()
  proxData.setDate(proxData.getDate() + 2)
  const labelDepois = `A partir de ${proxData.getDate()}/${String(proxData.getMonth() + 1).padStart(2, '0')}`

  const Aba = ({ id, label, n }: { id: 'hoje' | 'proximos' | 'finalizados'; label: string; n: number }) => (
    <button onClick={() => setAba(id)}
      className={`px-3 py-1.5 rounded-full text-xs font-medium transition flex items-center gap-1.5 ${
        aba === id ? 'bg-ink-900 text-white' : 'bg-white border border-ink-200 text-ink-700 hover:bg-ink-50'
      }`}>
      {label} <span className={`tabular-nums px-1.5 rounded-full text-[10px] ${aba === id ? 'bg-white/25' : 'bg-ink-100 text-ink-700'}`}>{n}</span>
    </button>
  )

  return (
    <div className="space-y-2">
      {/* Abas estilo painel ML */}
      <div className="flex flex-wrap gap-2">
        <Aba id="hoje" label="Envios de hoje" n={readyToShip + cancelada} />
        <Aba id="proximos" label="Próximos dias" n={proxTotal} />
        <Aba id="finalizados" label="Finalizadas" n={totFinalizados} />
      </div>

      {aba === 'hoje' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {/* Programada Coleta — cross_docking */}
          {programadaTotal > 0 && (
            <CardML
              titulo="Programada"
              subtitulo="Coleta hoje"
              total={programadaTotal}
              ativo={filtroStatus === 'ml_hoje_cross'}
              onClickTotal={() => setFiltroStatus('ml_hoje_cross')}
              accent="bg-amber-100 text-amber-900"
            >
              <MiniLink label="❌ Canceladas. Não enviar" qtd={cancelada}
                ativo={filtroStatus === 'ml_canceladas'} danger
                onClick={() => setFiltroStatus('ml_canceladas')} />
              <MiniLink label="⚠️ Atrasadas. Enviar" qtd={atrasada}
                ativo={filtroStatus === 'ml_sub_delayed'} danger
                onClick={() => setFiltroStatus('ml_sub_delayed')} />
              <MiniLink label="📄 NF-e para gerenciar" qtd={nfPendente}
                ativo={filtroStatus === 'ml_sub_invoice_pending'}
                onClick={() => setFiltroStatus('ml_sub_invoice_pending')} />
              <MiniLink label="🖨️ Etiquetas para imprimir" qtd={imprimir - flexImprimir}
                ativo={filtroStatus === 'ml_hoje_imprimir'}
                onClick={() => setFiltroStatus('ml_hoje_imprimir')} />
              <MiniLink label="📦 Prontas para coleta" qtd={programadaPronta}
                ativo={filtroStatus === 'ml_hoje_pronta'}
                onClick={() => setFiltroStatus('ml_hoje_pronta')} />
            </CardML>
          )}

          {/* Para Enviar — Flex */}
          {flexTotal > 0 && (
            <CardML
              titulo="Para enviar"
              subtitulo="Flex"
              total={flexTotal}
              ativo={filtroStatus === 'ml_hoje_flex'}
              onClickTotal={() => setFiltroStatus('ml_hoje_flex')}
              accent="bg-rose-100 text-rose-900"
            >
              <MiniLink label="🖨️ Etiquetas para imprimir" qtd={flexImprimir}
                ativo={filtroStatus === 'ml_flex_imprimir'}
                onClick={() => setFiltroStatus('ml_flex_imprimir')} />
              <MiniLink label="✅ Prontas para enviar" qtd={flexPronta}
                ativo={filtroStatus === 'ml_flex_pronta'}
                onClick={() => setFiltroStatus('ml_flex_pronta')} />
            </CardML>
          )}
        </div>
      )}

      {aba === 'proximos' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {amanhaTotal > 0 && (
            <CardML
              titulo="Coleta"
              subtitulo="Amanhã"
              total={amanhaTotal}
              ativo={filtroStatus === 'ml_amanha_total'}
              onClickTotal={() => setFiltroStatus('ml_amanha_total')}
              accent="bg-sky-100 text-sky-900"
            >
              <MiniLink label="🛠️ Em processamento" qtd={c.ml_amanha_proc || 0}
                ativo={filtroStatus === 'ml_amanha_proc'}
                onClick={() => setFiltroStatus('ml_amanha_proc')} />
              <MiniLink label="📦 Por envio padrão" qtd={c.ml_amanha_pronta || 0}
                ativo={filtroStatus === 'ml_amanha_pronta'}
                onClick={() => setFiltroStatus('ml_amanha_pronta')} />
            </CardML>
          )}
          {depoisTotal > 0 && (
            <CardML
              titulo="Coleta"
              subtitulo={labelDepois}
              total={depoisTotal}
              ativo={filtroStatus === 'ml_depois_total'}
              onClickTotal={() => setFiltroStatus('ml_depois_total')}
              accent="bg-indigo-100 text-indigo-900"
            >
              <MiniLink label="🛠️ Em processamento" qtd={c.ml_depois_proc || 0}
                ativo={filtroStatus === 'ml_depois_proc'}
                onClick={() => setFiltroStatus('ml_depois_proc')} />
              <MiniLink label="📦 Por envio padrão" qtd={c.ml_depois_pronta || 0}
                ativo={filtroStatus === 'ml_depois_pronta'}
                onClick={() => setFiltroStatus('ml_depois_pronta')} />
            </CardML>
          )}
        </div>
      )}

      {aba === 'finalizados' && (
        <div className="bg-white border border-ink-200 rounded-lg p-3 text-sm text-ink-600">
          <button onClick={() => setFiltroStatus('entregue')}
            className={`px-3 py-1.5 rounded font-medium ${filtroStatus === 'entregue' ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100'}`}>
            Ver pedidos finalizados ({totFinalizados})
          </button>
          <p className="mt-2 text-xs text-ink-500">Pedidos faturados / enviados / entregues — saíram da operação.</p>
        </div>
      )}
    </div>
  )
}

const STATUS_META: Record<string, { label: string; cor: string; ordem: number }> = {
  rascunho:       { label: 'Rascunho',      cor: 'bg-gray-100 text-gray-700',            ordem: 0 },
  bloqueado:      { label: 'Bloqueado',     cor: 'bg-rose-200 text-rose-900',            ordem: 1 },
  aberto:         { label: 'Aberto',        cor: 'bg-amber-100 text-amber-800',          ordem: 1 },
  aprovado:       { label: 'Aprovado',      cor: 'bg-blue-100 text-blue-800',            ordem: 2 },
  separacao:     { label: 'Em separação', cor: 'bg-indigo-100 text-indigo-800',        ordem: 3 },
  embalagem:      { label: 'Embalagem',     cor: 'bg-purple-100 text-purple-800',        ordem: 4 },
  aguardando_nf:  { label: 'Aguard. NF',    cor: 'bg-orange-100 text-orange-800',        ordem: 5 },
  faturado:       { label: 'Faturado',      cor: 'bg-cyan-100 text-cyan-800',            ordem: 6 },
  expedido:       { label: 'Expedido',      cor: 'bg-teal-100 text-teal-800',            ordem: 7 },
  entregue:       { label: 'Finalizados',   cor: 'bg-emerald-100 text-emerald-800',      ordem: 8 },
  confirmado:     { label: 'PDV',           cor: 'bg-emerald-50 text-emerald-700',       ordem: 9 },
  cancelado:      { label: 'Cancelado',     cor: 'bg-red-100 text-red-700',              ordem: 10 },
}

const CANAL_META: Record<string, { label: string; icon: any }> = {
  balcao:        { label: 'PDV',          icon: Store },
  digitado:      { label: 'Digitado',     icon: Laptop },
  celular:       { label: 'Celular',      icon: Smartphone },
  mercado_livre: { label: 'Mercado Livre', icon: Globe },
  tray:          { label: 'Tray',         icon: Globe },
  shopee:        { label: 'Shopee',       icon: Globe },
}

function tempoDesde(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  const ms = Date.now() - d.getTime()
  const min = Math.floor(ms / 60000)
  if (min < 1) return 'agora'
  if (min < 60) return `${min}m`
  const h = Math.floor(min / 60)
  if (h < 24) return `${h}h`
  const dias = Math.floor(h / 24)
  return `${dias}d`
}


// =============================================================
// PAGE ROOT
// =============================================================


function abrirPDF(path: string) {
  const token = localStorage.getItem('pain_token')
  fetch(`/api${path}`, { headers: { Authorization: `Bearer ${token}` } })
    .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob() })
    .then(blob => {
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank')
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    })
    .catch(e => toast.error(`Falha ao gerar PDF: ${e.message}`))
}

// FEATURE 30/06: imprime o PDF NA PRÓPRIA PÁGINA (iframe oculto), sem abrir outra
// aba. Mesma técnica do cupom: busca o blob (com auth), monta um iframe escondido,
// espera carregar e dispara print(). Fallback: nova aba se o print do iframe falhar.
function imprimirPDF(path: string) {
  const token = localStorage.getItem('pain_token')
  fetch(`/api${path}`, { headers: { Authorization: `Bearer ${token}` } })
    .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob() })
    .then(blob => {
      const url = URL.createObjectURL(blob)
      const iframe = document.createElement('iframe')
      iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;'
      iframe.src = url
      iframe.onload = () => {
        try {
          iframe.contentWindow?.focus()
          iframe.contentWindow?.print()
        } catch {
          window.open(url, '_blank')
        }
        // remove o iframe e libera a URL depois do diálogo de impressão.
        setTimeout(() => { try { document.body.removeChild(iframe) } catch {} URL.revokeObjectURL(url) }, 60000)
      }
      document.body.appendChild(iframe)
    })
    .catch(e => toast.error(`Falha ao imprimir: ${e.message}`))
}

export default function VendasPage() {
  const nav = useNavigate()
  const qc = useQueryClient()
  const marketplaceAtivo = useMarketplaceAtivo()
  // FEATURE 01/07: botão "Novo pedido" some quando a rota /vendas/novo está oculta
  // no menu (Configurações > Menu → empresa.menu_oculto). Espelha o Sidebar.
  const { empresa } = useAuth()
  const mostrarCanalTag = useMostrarCanalTag()   // tag Loja×Online na visão "Ambos" (09/07)
  const novoPedidoOculto = (empresa?.menu_oculto || []).includes('/vendas/novo')
  const desbloquearTodos = useMutation({
    mutationFn: async () => (await api.post('/vendas/pedidos/desbloquear-todos')).data,
    onSuccess: (r: any) => {
      toast.success(`${r.desbloqueados || 0} pedido(s) desbloqueado(s)`)
      qc.invalidateQueries({ queryKey: ['pedidos'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao desbloquear'),
  })
  const [filtroStatus, setFiltroStatus] = useState<string>('ativos')
  const [filtroCanal, setFiltroCanal] = useState<string>(() => {
    // Dashboard "Seu negócio" abre esta tela já com o canal filtrado (?canal=balcao = PDV). (06/07)
    try { return new URLSearchParams(window.location.search).get('canal') || '' } catch { return '' }
  })
  const [filtroTipoVenda, setFiltroTipoVenda] = useState<string>('')  // '' = todos | fiscal (com nota) | simples (sem nota)
  const [filtroVendedores, setFiltroVendedores] = useState<Set<number>>(new Set())  // ids de vendedores selecionados (multi)
  const [vendPopAberto, setVendPopAberto] = useState(false)
  const [buscaVend, setBuscaVend] = useState('')
  const [filtroRotas, setFiltroRotas] = useState<Set<number>>(new Set())
  const [rotaPopAberto, setRotaPopAberto] = useState(false)
  const [buscaRota, setBuscaRota] = useState('')
  const [filtroBairros, setFiltroBairros] = useState<Set<string>>(new Set())  // bairros selecionados (multi)
  const [bairroPopAberto, setBairroPopAberto] = useState(false)
  const [buscaBairro, setBuscaBairro] = useState('')
  // Pré-preenche a busca via ?busca=<# ou cliente> (ex.: PDV abre esta tela em
  // nova aba pra cancelar venda com cupom fiscal).
  const [busca, setBusca] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('busca') || '' } catch { return '' }
  })
  const [filtroData, setFiltroData] = useState<'hoje' | '7d' | '30d' | 'mes' | 'tudo' | 'custom'>(() => {
    // FIX (06/07): o dashboard abre esta tela com ?periodo=hoje|mes|custom já aplicado.
    // Antes caía sempre em 'tudo' e o card parecia levar a uma tela "vazia"/sem filtro.
    try {
      const p = new URLSearchParams(window.location.search).get('periodo')
      if (p && ['hoje', '7d', '30d', 'mes', 'custom', 'tudo'].includes(p)) return p as any
    } catch {}
    return 'tudo'
  })
  const [dataDe, setDataDe] = useState('')
  const [dataAte, setDataAte] = useState('')
  const [customRange, setCustomRange] = useState<{ de: string; ate: string }>(() => {
    // ?de=YYYY-MM-DD&ate=YYYY-MM-DD (ex.: card "Mês anterior" do dashboard). (06/07)
    try {
      const q = new URLSearchParams(window.location.search)
      return { de: q.get('de') || '', ate: q.get('ate') || '' }
    } catch { return { de: '', ate: '' } }
  })
  // range enviado ao backend (filtra por data do pedido). Chips aplicam na hora; custom no botão Pesquisar.
  const rangeBackend = (() => {
    const fmt = (d: Date) => d.toISOString().slice(0, 10)
    const a = new Date()
    if (filtroData === 'hoje') return { data_de: fmt(a), data_ate: fmt(a) }
    if (filtroData === '7d') return { data_de: fmt(new Date(a.getFullYear(), a.getMonth(), a.getDate() - 6)) }
    if (filtroData === '30d') return { data_de: fmt(new Date(a.getFullYear(), a.getMonth(), a.getDate() - 29)) }
    if (filtroData === 'mes') return { data_de: fmt(new Date(a.getFullYear(), a.getMonth(), 1)) }
    if (filtroData === 'custom') return { data_de: customRange.de || undefined, data_ate: customRange.ate || undefined }
    return {}  // tudo
  })()
  const [pedidoAberto, setPedidoAberto] = useState<number | null>(null)
  const [cupomPedido, setCupomPedido] = useState<{ id: number; numero: number } | null>(null)
  // Snapshot dos ids no clique pra o relatório não mudar se o filtro mudar com o modal aberto
  const [relatorio, setRelatorio] = useState<{ ids: number[]; titulo: string } | null>(null)
  const [faturamentoAberto, setFaturamentoAberto] = useState(false)
  const [colsOcultas, setColsOcultasRaw] = useState<Set<ColunaId>>(() => carregarColunasOcultas())
  const setColsOcultas = (s: Set<ColunaId>) => {
    setColsOcultasRaw(s)
    try { localStorage.setItem('pain_vendas_cols_hidden', JSON.stringify([...s])) } catch {}
  }
  const ocultar = (id: ColunaId) => colsOcultas.has(id)
  // ============================================================
  // Emissao em lote de NFe propria (modo opcional)
  // ============================================================
  const [modoSelecao, setModoSelecao] = useState(false)
  const [selecionados, setSelecionados] = useState<Set<number>>(new Set())
  const [loteProgresso, setLoteProgresso] = useState<string | null>(null)
  const [loteResultado, setLoteResultado] = useState<any[] | null>(null)

  // Totais da carga (valor + peso) dos pedidos selecionados — mostra antes de montar
  const idsSelecao = Array.from(selecionados).sort((a, b) => a - b)
  const { data: totaisSelecao } = useQuery<any>({
    queryKey: ['carga-totais-selecao', idsSelecao],
    queryFn: async () => (await api.post('/vendas/cargas/totais-selecao', { pedido_ids: idsSelecao })).data,
    enabled: idsSelecao.length > 0,
    placeholderData: (prev: any) => prev,
  })

  const toggleSelecao = (pid: number) => {
    setSelecionados((prev) => {
      const n = new Set(prev)
      if (n.has(pid)) n.delete(pid)
      else n.add(pid)
      return n
    })
  }

  const limparSelecao = () => { setSelecionados(new Set()); setModoSelecao(false) }
  const gerarCargaSelecionados = async () => {
    const ids = Array.from(selecionados)
    if (!ids.length) return
    // Pergunta o nome da ROTA (controle interno) antes de criar
    const rotaInput = window.prompt(
      `Vai gerar carga com ${ids.length} pedido(s).\n\nNome da ROTA (controle interno — pode deixar em branco):`,
      '',
    )
    if (rotaInput === null) return // cancelou
    const rota = (rotaInput || '').trim()
    const dataHoje = new Date().toLocaleDateString('pt-BR')
    const nome = rota ? `Carga ${dataHoje} — ${rota}` : `Carga ${dataHoje}`
    try {
      const r = await api.post('/vendas/cargas/distribuicao', {
        pedido_ids: ids, nome,
      })
      toast.success(`Carga #${r.data?.numero ?? ''} criada com ${ids.length} pedido(s) — fature em lote na tela da carga`)
      limparSelecao()
      nav('/expedicao/cargas')
    } catch (e: any) { toast.error(e?.response?.data?.detail || 'Erro ao gerar carga') }
  }

  // Só pode entrar numa carga nova se ainda não estiver vinculado a outra (carga_id vazio).
  const podeSelecionar = (p: any) => !p.carga_id && ['aberto','aprovado','separacao','embalagem','aguardando_nf','faturado'].includes(p.status)

  const executarLote = async (acao: 'preview' | 'danfe' | 'danfe-simples') => {
    const ids = Array.from(selecionados)
    if (ids.length === 0) return
    const resultados: any[] = []
    for (let i = 0; i < ids.length; i++) {
      const pid = ids[i]
      setLoteProgresso(`Processando ${i + 1}/${ids.length}...`)
      try {
        if (acao === 'preview') {
          const r = await api.get(`/nfe-proprio/preview-xml/${pid}`)
          resultados.push({ pid, ok: true, alertas: r.data.alertas || [], chave: r.data.chave_acesso })
        } else {
          // danfe: abre PDF em nova aba
          const simples = acao === 'danfe-simples'
          const resp = await api.get(`/nfe-proprio/danfe/${pid}${simples ? '?simples=true' : ''}`, {
            responseType: 'blob',
          })
          const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }))
          window.open(url, '_blank')
          setTimeout(() => URL.revokeObjectURL(url), 30000)
          resultados.push({ pid, ok: true })
        }
      } catch (e: any) {
        resultados.push({ pid, ok: false, erro: e?.response?.data?.detail || 'erro' })
      }
    }
    setLoteProgresso(null)
    if (acao === 'preview') {
      setLoteResultado(resultados)
    } else {
      toast.success(`${resultados.filter(r => r.ok).length}/${ids.length} DANFEs abertos em novas abas`)
      limparSelecao()
    }
  }


  const { data: vendedores = [] } = useQuery<{ id: number; nome: string; codigo_vendedor: string | null }[]>({
    queryKey: ['vendedores-filtro'],
    queryFn: async () => (await api.get('/vendas/vendedores')).data,
  })
  const vendedorIdsCsv = Array.from(filtroVendedores).join(',')

  const filialAtualId = useFilial(s => s.filialAtual?.id ?? null)
  const { data: pedidos = [], isLoading } = useQuery<Pedido[]>({
    queryKey: ['pedidos', rangeBackend.data_de, rangeBackend.data_ate, vendedorIdsCsv, filialAtualId],
    queryFn: async () => (await api.get('/vendas', { params: { ...rangeBackend, limit: 2000, vendedor_ids: vendedorIdsCsv || undefined } })).data,
    refetchOnWindowFocus: true,    // ao voltar pra aba, atualiza
    refetchOnMount: 'always',      // ao montar componente, sempre busca fresco
    staleTime: 5000,               // dados <5s considerados frescos
  })

  // Contadores OFICIAIS do painel ML — chamadas diretas em /orders/search.
  // Numerais idênticos aos que o seller vê no painel do Mercado Livre.
  const { data: contadoresML } = useQuery<Record<string, number>>({
    queryKey: ['ml-contadores-painel'],
    queryFn: async () => (await api.get('/ml/contadores-painel')).data,
    enabled: !!marketplaceAtivo,
    refetchOnWindowFocus: true,
    staleTime: 60_000,             // backend já cacheia 60s
    retry: 1,
  })

  // Quando filtro for por substatus oficial ML, busca os order_ids da API e filtra.
  // (Alguns substatus como 'delayed' são interpretação do ML que não vem no /shipments/{id}.)
  const subFiltro = filtroStatus.startsWith('ml_sub_')
    ? filtroStatus.slice('ml_sub_'.length)
    : null
  const { data: orderIdsSub } = useQuery<{ order_ids: string[] }>({
    queryKey: ['ml-order-ids-substatus', subFiltro],
    queryFn: async () => (await api.get(`/ml/order-ids-substatus?substatus=${subFiltro}`)).data,
    enabled: !!subFiltro && !!marketplaceAtivo,
    staleTime: 60_000,
  })

  const { data: clientes = [] } = useQuery<any[]>({
    queryKey: ['clientes-all'],
    queryFn: async () => (await api.get('/cadastros/pessoas', { params: { tipo: 'cliente' } })).data,
  })
  const mapCliente = useMemo(() => {
    const m: Record<number, string> = {}
    clientes.forEach(c => { m[c.id] = c.nome })
    return m
  }, [clientes])

  const { data: depositos = [] } = useQuery<any[]>({
    queryKey: ['depositos'],
    queryFn: async () => (await api.get('/estoque/depositos')).data,
  })
  const mapDeposito = useMemo(() => {
    const m: Record<number, any> = {}
    depositos.forEach(d => { m[d.id] = d })
    return m
  }, [depositos])
  const mostrarDeposito = depositos.length > 1

  const filtrados = useMemo(() => {
    let list = pedidos
    const expedido = (p: Pedido) => p.canal_origem === 'mercado_livre' &&
      ['shipped','delivered','not_delivered','cancelled'].includes(p.coleta_status || '')

    if (filtroStatus === 'ativos') {
      list = list.filter(p => {
        if (['faturado', 'entregue', 'cancelado', 'confirmado'].includes(p.status)) return false
        if (expedido(p)) return false
        return true
      })
    } else if (filtroStatus === 'ml_finalizados') {
      list = list.filter(p => p.canal_origem === 'mercado_livre' && expedido(p))
    } else if (filtroStatus === 'pdv') {
      // Cancelado sai do PDV — ele já aparece no chip "Cancelado" (não duplicar).
      list = list.filter(p => p.canal_origem === 'balcao' && p.status !== 'cancelado')
    } else if (filtroStatus.startsWith('ml_')) {
      // Filtros ML por bucket + categoria
      list = list.filter(p => {
        if (p.canal_origem !== 'mercado_livre') return false
        const cat = categoriaML(p)
        const bucket = bucketMLTempo(p)
        const aberto = !expedido(p) && p.status !== 'cancelado'
        // Filtros por substatus oficial do ML (nomes ml_sub_*)
        // Usa lista de order_ids retornada pela API ML — paridade 100% com painel.
        if (filtroStatus.startsWith('ml_sub_')) {
          if (!orderIdsSub?.order_ids) return false
          const set = new Set(orderIdsSub.order_ids)
          return !!p.ml_order_id && set.has(p.ml_order_id)
        }
        switch (filtroStatus) {
          case 'ml': return aberto || cat === 'cancelada'
          case 'ml_sai_hoje':
          case 'ml_hoje_total':
            return cat === 'cancelada' || cat === 'atrasada' ||
              (aberto && (p.coleta_modalidade === 'self_service' || bucket === 'hoje'))
          case 'ml_hoje_cross':
            return cat === 'atrasada' ||
              (aberto && p.coleta_modalidade !== 'self_service' && bucket === 'hoje')
          case 'ml_hoje_nf':
            return aberto && p.coleta_modalidade !== 'self_service' && bucket === 'hoje' && cat === 'nf_pendente'
          case 'ml_hoje_imprimir':
            return aberto && p.coleta_modalidade !== 'self_service' && bucket === 'hoje' && cat === 'imprimir'
          case 'ml_hoje_pronta':
            return aberto && p.coleta_modalidade !== 'self_service' && bucket === 'hoje' && cat === 'pronta'
          case 'ml_hoje_atrasada':
          case 'ml_atrasado':
            return cat === 'atrasada'
          case 'ml_hoje_cancelada':
          case 'ml_canceladas':
            return cat === 'cancelada'
          case 'ml_hoje_flex':
          case 'ml_flex':
            return aberto && p.coleta_modalidade === 'self_service'
          case 'ml_flex_imprimir':
            return aberto && cat === 'flex_imprimir'
          case 'ml_flex_pronta':
            return aberto && cat === 'flex_pronta'
          case 'ml_amanha_total':
          case 'ml_agendado':  // legado
            return aberto && p.coleta_modalidade !== 'self_service' && bucket === 'amanha'
          case 'ml_amanha_nf':
            return aberto && bucket === 'amanha' && cat === 'nf_pendente'
          case 'ml_amanha_proc':
            return aberto && bucket === 'amanha' && cat === 'em_proc'
          case 'ml_amanha_pronta':
            return aberto && bucket === 'amanha' && cat === 'pronta'
          case 'ml_depois_total':
            return aberto && p.coleta_modalidade !== 'self_service' && bucket === 'depois'
          case 'ml_depois_nf':
            return aberto && bucket === 'depois' && cat === 'nf_pendente'
          case 'ml_depois_proc':
            return aberto && bucket === 'depois' && cat === 'em_proc'
          case 'ml_depois_pronta':
            return aberto && bucket === 'depois' && cat === 'pronta'
          default: return false
        }
      })
    } else if (filtroStatus === 'separacao') {
      // Em separação = status='separacao' OU iniciou separação e não concluiu (qualquer status)
      // Pega pedidos que entraram em onda mesmo que ML/expedição já tenha avançado o status.
      list = list.filter(p =>
        p.status === 'separacao'
        || (p.separacao_iniciada_em && !p.separacao_concluida_em)
      )
    } else if (filtroStatus && filtroStatus !== 'todos') {
      list = list.filter(p => p.status === filtroStatus)
    }
    if (filtroCanal) list = list.filter(p => p.canal_origem === filtroCanal)
    if (filtroTipoVenda) list = list.filter(p => p.tipo_venda === filtroTipoVenda)
    if (filtroRotas.size) list = list.filter(p => p.rota_id != null && filtroRotas.has(p.rota_id))
    if (filtroBairros.size) list = list.filter(p => (p.cliente_bairro || '').trim() !== '' && filtroBairros.has((p.cliente_bairro || '').trim()))
    // Filtro por data de criacao
    // filtro por data agora é feito no BACKEND (rangeBackend → params da query)
    if (busca) {
      const q = busca.toLowerCase()
      const qDigits = q.replace(/\D/g, '')
      list = list.filter(p =>
        String(p.numero).includes(q) ||
        (p.cliente_nome || mapCliente[p.cliente_id] || '').toLowerCase().includes(q) ||
        (qDigits && (p.cliente_documento || '').replace(/\D/g, '').includes(qDigits)) ||
        (p.ml_order_id || '').toLowerCase().includes(q) ||
        (p.mp_payment_id || '').toLowerCase().includes(q),
      )
    }
    // Ordem: mais recentes primeiro
    return [...list].sort((a, b) => b.id - a.id)
  }, [pedidos, filtroStatus, filtroCanal, filtroTipoVenda, filtroRotas, filtroBairros, busca, mapCliente, filtroData, dataDe, dataAte])

  // Ordenação por coluna (clique no cabeçalho)
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null)
  const sortVal = (p: any, key: string) => {
    switch (key) {
      case 'numero': return p.numero || 0
      case 'cliente': return (mapCliente[p.cliente_id] || '').toLowerCase()
      case 'data': return p.confirmado_em || p.criado_em || ''
      case 'tipo_venda': return p.tipo_venda || ''
      case 'rota': return (p.rota_nome || '').toLowerCase()
      case 'uf': return (p.cliente_uf || '').toLowerCase()
      case 'bairro': return (p.cliente_bairro || '').toLowerCase()
      case 'vendedor': return (p.vendedor_codigo || '').toLowerCase()
      case 'canal': return p.canal_origem || ''
      case 'deposito': return p.deposito_id || 0
      case 'total': return valorLiquido(p)  // ordena pelo valor líquido (desconta bike da troca)
      case 'status': return p.status || ''
      case 'tempo': return p.criado_em || ''
      default: return ''
    }
  }
  const filtradosSorted = useMemo(() => {
    if (!sort) return filtrados
    const arr = [...filtrados]
    arr.sort((a, b) => {
      const va = sortVal(a, sort.key), vb = sortVal(b, sort.key)
      if (va < vb) return sort.dir === 'asc' ? -1 : 1
      if (va > vb) return sort.dir === 'asc' ? 1 : -1
      return 0
    })
    return arr
  }, [filtrados, sort, mapCliente])
  // Paginação SÓ da exibição (100/página). Filtro, seleção e cards usam a lista INTEIRA.
  const PAGE_SIZE = 100
  const [pagina, setPagina] = useState(0)
  const totalPaginas = Math.max(1, Math.ceil(filtradosSorted.length / PAGE_SIZE))
  const paginaSegura = Math.min(pagina, totalPaginas - 1)
  const filtradosPagina = useMemo(
    () => filtradosSorted.slice(paginaSegura * PAGE_SIZE, paginaSegura * PAGE_SIZE + PAGE_SIZE),
    [filtradosSorted, paginaSegura])
  // volta pra 1ª página quando o filtro/ordenação muda
  useEffect(() => { setPagina(0) }, [filtrados, sort])
  // Totais da lista filtrada (refletem o filtro atual) — mostrados no topo
  const resumo = useMemo(() => {
    let valor = 0, itens = 0
    for (const p of filtrados) {
      valor += valorLiquido(p)  // total da lista já líquido do desconto da bike
      itens += Array.isArray(p.itens) ? p.itens.reduce((s: number, it: any) => s + Number(it?.quantidade || 0), 0) : 0
    }
    return { pedidos: filtrados.length, valor, itens }
  }, [filtrados])
  const toggleSort = (key: string) => setSort(s => s?.key === key ? (s.dir === 'asc' ? { key, dir: 'desc' } : null) : { key, dir: 'asc' })
  const arrow = (key: string) => sort?.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ' ↕'

  const contadoresStatus = useMemo(() => {
    const c: Record<string, number> = {
      ativos: 0, pdv: 0, todos: pedidos.length,
      ml: 0, ml_finalizados: 0,
      // Buckets temporais (paridade painel ML)
      ml_hoje_total: 0, ml_amanha_total: 0, ml_depois_total: 0,
      // Subdivisões "Hoje" cross_docking
      ml_hoje_cross: 0, ml_hoje_nf: 0, ml_hoje_imprimir: 0, ml_hoje_pronta: 0,
      ml_hoje_atrasada: 0, ml_hoje_cancelada: 0,
      // Subdivisões "Hoje" flex
      ml_hoje_flex: 0, ml_flex_imprimir: 0, ml_flex_pronta: 0,
      // Subdivisões "Amanhã"
      ml_amanha_nf: 0, ml_amanha_proc: 0, ml_amanha_pronta: 0,
      // Subdivisões "Depois"
      ml_depois_nf: 0, ml_depois_proc: 0, ml_depois_pronta: 0,
      // Atalhos legados (mantidos pra compatibilidade do filtro)
      ml_canceladas: 0, ml_atrasado: 0, ml_sai_hoje: 0, ml_flex: 0,
    }
    pedidos.forEach(p => {
      const cat = categoriaML(p)
      const bucket = bucketMLTempo(p)
      const finalizado = ['faturado', 'entregue', 'cancelado', 'confirmado'].includes(p.status)
      const expedido = p.canal_origem === 'mercado_livre' &&
        ['shipped','delivered','not_delivered','cancelled'].includes(p.coleta_status || '')

      if (!finalizado && !expedido) c.ativos++
      if (p.canal_origem === 'balcao' && p.status !== 'cancelado') c.pdv++
      if (p.canal_origem === 'mercado_livre' && !expedido && p.status !== 'cancelado') c.ml++
      if (expedido) c.ml_finalizados++

      // Cancelada recente (entra no card "hoje" como alerta, não conta no ml total)
      if (cat === 'cancelada') {
        c.ml_canceladas++
        c.ml_hoje_cancelada++
      }
      // Atrasada (ready_for_pickup vencido)
      if (cat === 'atrasada') {
        c.ml_atrasado++
        c.ml_hoje_atrasada++
        c.ml_hoje_cross++
        c.ml_hoje_total++
      }

      // Distribui por bucket temporal — só para abertos
      if (!finalizado && !expedido && p.canal_origem === 'mercado_livre' && cat !== 'cancelada' && cat !== 'atrasada') {
        if (p.coleta_modalidade === 'self_service') {
          // Flex sempre entra no bucket "hoje"
          c.ml_hoje_total++
          c.ml_hoje_flex++
          c.ml_flex++
          if (cat === 'flex_imprimir') c.ml_flex_imprimir++
          else if (cat === 'flex_pronta') c.ml_flex_pronta++
        } else {
          // Cross_docking & outros → divide por last_updated
          if (bucket === 'hoje') {
            c.ml_hoje_total++
            c.ml_hoje_cross++
            if (cat === 'nf_pendente') c.ml_hoje_nf++
            else if (cat === 'imprimir') c.ml_hoje_imprimir++
            else if (cat === 'pronta') c.ml_hoje_pronta++
          } else if (bucket === 'amanha') {
            c.ml_amanha_total++
            if (cat === 'nf_pendente') c.ml_amanha_nf++
            else if (cat === 'em_proc') c.ml_amanha_proc++
            else if (cat === 'pronta') c.ml_amanha_pronta++
          } else {
            c.ml_depois_total++
            if (cat === 'nf_pendente') c.ml_depois_nf++
            else if (cat === 'em_proc') c.ml_depois_proc++
            else if (cat === 'pronta') c.ml_depois_pronta++
          }
        }
      }
      c.ml_sai_hoje = c.ml_hoje_total
      c[p.status] = (c[p.status] || 0) + 1
      // Separação ampliada: contabiliza qualquer pedido com separacao_iniciada e não concluída,
      // mesmo se status já é entregue/expedido (carga aberta sem conclusão formal).
      if (p.separacao_iniciada_em && !p.separacao_concluida_em && p.status !== 'separacao') {
        c.separacao = (c.separacao || 0) + 1
      }
    })
    return c
  }, [pedidos])

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-ink-900">Pedidos</h1>
          <p className="text-sm text-ink-500">Fluxo completo de vendas: do pedido digitado à entrega.</p>
        </div>
        <div className="flex gap-2">
          {(contadoresStatus.bloqueado || 0) > 0 && (
            <button
              onClick={() => { if (confirm(`Desbloquear todos os ${contadoresStatus.bloqueado} pedidos bloqueados?`)) desbloquearTodos.mutate() }}
              disabled={desbloquearTodos.isPending}
              className="btn-ghost flex items-center gap-1 text-emerald-700 disabled:opacity-50"
              title="Desbloqueia de uma vez todos os pedidos bloqueados (restaura o status anterior de cada um)">
              🔓 Desbloquear todos ({contadoresStatus.bloqueado})
            </button>
          )}
          <button onClick={() => nav('/vendas/separacao')}
            className="btn-ghost flex items-center gap-1">
            🗺️ Separação
          </button>
          <button onClick={() => nav('/vendas/ondas')}
            className="btn-ghost flex items-center gap-1">
            🌊 Ondas
          </button>
          <button onClick={() => setFaturamentoAberto(true)}
            className="btn-ghost flex items-center gap-1" title="Cargas em aberto que faltam faturar">
            🧾 Faturamento
          </button>
          <button
            onClick={() => { if (modoSelecao) limparSelecao(); else setModoSelecao(true) }}
            className={`btn-ghost flex items-center gap-1 ${modoSelecao ? 'bg-brand-100 text-brand-700' : ''}`}
            title="Selecionar varios pedidos para emissao em lote de NFe/DANFE"
          >
            {modoSelecao ? <CheckSquare size={14} /> : <Square size={14} />}
            {modoSelecao ? 'Cancelar selecao' : 'Selecionar multiplos'}
          </button>
          {!novoPedidoOculto && (
            <button onClick={() => nav('/vendas/novo')}
              className="btn-primary">
              <Plus size={14} /> Novo pedido
            </button>
          )}
        </div>
      </div>

      {faturamentoAberto && <ModalFaturamentoPendente onClose={() => setFaturamentoAberto(false)} />}

      {/* Painel OPERACIONAL — contadores OFICIAIS direto da API ML */}
      {marketplaceAtivo && contadoresML && (
        <PainelML
          c={contadoresStatus}
          ml={contadoresML}
          filtroStatus={filtroStatus}
          setFiltroStatus={setFiltroStatus} />
      )}

      {/* Filtros (agrupados num card pra dar respiro) */}
      <div className="card card-body space-y-3">
        <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-semibold uppercase text-ink-400 mr-1 self-center">Status</span>
        <FiltroChip label={`Ativos (${contadoresStatus.ativos || 0})`}
          ativo={filtroStatus === 'ativos'}
          onClick={() => setFiltroStatus('ativos')} />
        {[
          // 06/07 (pedido do usuário): chip "Em separação" removido — filtro não é usado.
          // O status 'separacao' segue existindo no pipeline; só o chip de filtro sumiu.
          'bloqueado', 'aberto', 'aprovado', 'embalagem',
          'aguardando_nf', 'faturado', 'expedido', 'entregue', 'cancelado',
        ].map(s => {
          const qtd = contadoresStatus[s] || 0
          if (qtd === 0 && filtroStatus !== s) return null
          const m = STATUS_META[s]
          return (
            <FiltroChip key={s}
              label={`${m.label} (${qtd})`}
              ativo={filtroStatus === s}
              cor={m.cor}
              onClick={() => setFiltroStatus(s)} />
          )
        })}
        <FiltroChip label={`PDV (${contadoresStatus.pdv || 0})`}
          ativo={filtroStatus === 'pdv'}
          onClick={() => setFiltroStatus('pdv')} />
        <FiltroChip label="Todos"
          ativo={filtroStatus === 'todos'}
          onClick={() => setFiltroStatus('todos')} />
        </div>

        <div className="border-t border-ink-100" />

        {/* Busca + canal + tipo + rota + data */}
        <div className="flex gap-3 items-center flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
          <input className="input pl-8" placeholder="Buscar por # ou cliente..."
            value={busca} onChange={e => setBusca(e.target.value)}
            onKeyDown={enterAbrePrimeiro(filtradosPagina, p => {
              if (modoSelecao) { if (podeSelecionar(p)) toggleSelecao(p.id) }
              else setPedidoAberto(p.id)
            })} />
        </div>
        <select className="input w-48" value={filtroCanal}
          onChange={e => setFiltroCanal(e.target.value)}>
          <option value="">Todos os canais</option>
          <option value="digitado">Digitado</option>
          <option value="celular">Celular</option>
          <option value="balcao">PDV</option>
        </select>
        <select className="input w-44" value={filtroTipoVenda}
          onChange={e => setFiltroTipoVenda(e.target.value)}>
          <option value="">Com e sem nota</option>
          <option value="fiscal">Com nota (NF-e)</option>
          <option value="simples">Sem nota</option>
        </select>
        <div className="relative">
          <button onClick={() => setVendPopAberto(o => !o)}
            className={`input w-48 text-left text-sm flex items-center justify-between ${filtroVendedores.size ? 'text-brand-700 font-medium' : 'text-ink-600'}`}>
            <span>{filtroVendedores.size ? `${filtroVendedores.size} vendedor(es)` : 'Todos os vendedores'}</span>
            <span className="text-ink-400">▾</span>
          </button>
          {vendPopAberto && (
            <div className="absolute z-30 mt-1 w-64 bg-white border border-ink-200 rounded-lg shadow-lg p-2">
              <input autoFocus className="input input-sm w-full mb-2" placeholder="Buscar vendedor..."
                value={buscaVend} onChange={e => setBuscaVend(e.target.value)} />
              <div className="flex justify-between text-[11px] mb-1 px-1">
                <button className="text-brand-700" onClick={() => setFiltroVendedores(new Set())}>Limpar</button>
                <button className="text-ink-500" onClick={() => setVendPopAberto(false)}>Fechar</button>
              </div>
              <div className="max-h-56 overflow-y-auto">
                {vendedores
                  .filter(v => {
                    const t = buscaVend.toLowerCase()
                    return !t || (v.nome || '').toLowerCase().includes(t) || String(v.codigo_vendedor || '').includes(t)
                  })
                  .sort((a, b) => (a.nome || '').localeCompare(b.nome || ''))
                  .map(v => (
                    <label key={v.id} className="flex items-center gap-2 px-1 py-1 text-sm hover:bg-ink-50 rounded cursor-pointer">
                      <input type="checkbox" checked={filtroVendedores.has(v.id)}
                        onChange={() => setFiltroVendedores(s => { const n = new Set(s); n.has(v.id) ? n.delete(v.id) : n.add(v.id); return n })} />
                      <span className="truncate">{v.codigo_vendedor ? `${v.codigo_vendedor} — ` : ''}{v.nome}</span>
                    </label>
                  ))}
              </div>
            </div>
          )}
        </div>
        <div className="relative">
          <button onClick={() => setRotaPopAberto(o => !o)}
            className={`input w-44 text-left text-sm flex items-center justify-between ${filtroRotas.size ? 'text-brand-700 font-medium' : 'text-ink-600'}`}>
            <span>{filtroRotas.size ? `${filtroRotas.size} rota(s)` : 'Todas as rotas'}</span>
            <span className="text-ink-400">▾</span>
          </button>
          {rotaPopAberto && (
            <div className="absolute z-30 mt-1 w-60 bg-white border border-ink-200 rounded-lg shadow-lg p-2">
              <input autoFocus className="input input-sm w-full mb-2" placeholder="Buscar rota..."
                value={buscaRota} onChange={e => setBuscaRota(e.target.value)} />
              <div className="flex justify-between text-[11px] mb-1 px-1">
                <button className="text-brand-700" onClick={() => setFiltroRotas(new Set())}>Limpar</button>
                <button className="text-ink-500" onClick={() => setRotaPopAberto(false)}>Fechar</button>
              </div>
              <div className="max-h-56 overflow-y-auto">
                {Array.from(new Map(pedidos.filter(p => p.rota_id).map(p => [p.rota_id as number, p.rota_nome || `Rota ${p.rota_id}`])).entries())
                  .filter(([, n]) => !buscaRota || (n || '').toLowerCase().includes(buscaRota.toLowerCase()))
                  .sort((a, b) => (a[1] || '').localeCompare(b[1] || ''))
                  .map(([rid, rnome]) => (
                    <label key={rid} className="flex items-center gap-2 px-1 py-1 text-sm hover:bg-ink-50 rounded cursor-pointer">
                      <input type="checkbox" checked={filtroRotas.has(rid)}
                        onChange={() => setFiltroRotas(s => { const n = new Set(s); n.has(rid) ? n.delete(rid) : n.add(rid); return n })} />
                      <span className="truncate">{rnome}</span>
                    </label>
                  ))}
              </div>
            </div>
          )}
        </div>
        <div className="relative">
          <button onClick={() => setBairroPopAberto(o => !o)}
            className={`input w-44 text-left text-sm flex items-center justify-between ${filtroBairros.size ? 'text-brand-700 font-medium' : 'text-ink-600'}`}>
            <span>{filtroBairros.size ? `${filtroBairros.size} bairro(s)` : 'Todos os bairros'}</span>
            <span className="text-ink-400">▾</span>
          </button>
          {bairroPopAberto && (
            <div className="absolute z-30 mt-1 w-60 bg-white border border-ink-200 rounded-lg shadow-lg p-2">
              <input autoFocus className="input input-sm w-full mb-2" placeholder="Buscar bairro..."
                value={buscaBairro} onChange={e => setBuscaBairro(e.target.value)} />
              <div className="flex justify-between text-[11px] mb-1 px-1">
                <button className="text-brand-700" onClick={() => setFiltroBairros(new Set())}>Limpar</button>
                <button className="text-ink-500" onClick={() => setBairroPopAberto(false)}>Fechar</button>
              </div>
              <div className="max-h-56 overflow-y-auto">
                {Array.from(new Set(pedidos.map(p => (p.cliente_bairro || '').trim()).filter(Boolean)))
                  .filter(b => !buscaBairro || b.toLowerCase().includes(buscaBairro.toLowerCase()))
                  .sort((a, b) => a.localeCompare(b))
                  .map(b => (
                    <label key={b} className="flex items-center gap-2 px-1 py-1 text-sm hover:bg-ink-50 rounded cursor-pointer">
                      <input type="checkbox" checked={filtroBairros.has(b)}
                        onChange={() => setFiltroBairros(s => { const n = new Set(s); n.has(b) ? n.delete(b) : n.add(b); return n })} />
                      <span className="truncate">{b}</span>
                    </label>
                  ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 bg-ink-50 rounded-md p-1">
          {([
            ['hoje', 'Hoje'],
            ['7d', '7 dias'],
            ['30d', '30 dias'],
            ['mes', 'Este mês'],
            ['tudo', 'Tudo'],
            ['custom', 'Personalizado'],
          ] as const).map(([v, label]) => (
            <button
              key={v}
              onClick={() => setFiltroData(v)}
              className={`px-2 py-1 text-xs rounded transition ${
                filtroData === v
                  ? 'bg-white text-brand-700 font-medium shadow-sm'
                  : 'text-ink-600 hover:text-ink-900'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        {filtroData === 'custom' && (
          <div className="flex items-center gap-1 text-xs">
            <input type="date" className="input input-sm" value={dataDe}
              onChange={e => setDataDe(e.target.value)} />
            <span className="text-ink-500">até</span>
            <input type="date" className="input input-sm" value={dataAte}
              onChange={e => setDataAte(e.target.value)} />
            <button onClick={() => setCustomRange({ de: dataDe, ate: dataAte })}
              disabled={!dataDe && !dataAte}
              className="btn-primary !py-1 !px-3 text-xs disabled:opacity-50">
              <Search size={12} /> Pesquisar
            </button>
          </div>
        )}
        </div>
      </div>

      {/* Totais da lista (refletem o filtro atual) */}
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="flex-1 min-w-[140px] rounded-xl border border-ink-200 bg-white px-4 py-2">
          <div className="text-[11px] uppercase tracking-wide text-ink-400">Pedidos</div>
          <div className="text-xl font-bold text-ink-800">{resumo.pedidos.toLocaleString('pt-BR')}</div>
        </div>
        <div className="flex-1 min-w-[160px] rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2">
          <div className="text-[11px] uppercase tracking-wide text-emerald-700">Valor total</div>
          <div className="text-xl font-bold text-emerald-800">{resumo.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>
        </div>
        <div className="flex-1 min-w-[140px] rounded-xl border border-ink-200 bg-white px-4 py-2">
          <div className="text-[11px] uppercase tracking-wide text-ink-400">Qtde de itens</div>
          <div className="text-xl font-bold text-ink-800">{resumo.itens.toLocaleString('pt-BR')}</div>
          <div className="text-[10px] text-ink-400 leading-tight">soma das quantidades</div>
        </div>
        <button
          onClick={() => {
            const ids = filtrados.map(p => p.id)
            if (!ids.length) { toast.error('Nenhum pedido na lista pra gerar relatório'); return }
            setRelatorio({ ids, titulo: 'Relatório de Pedidos' })
          }}
          className="h-[52px] rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-semibold px-4 flex items-center gap-2">
          📄 Gerar PDF do relatório
        </button>
      </div>

      {/* Tabela */}
      <div className="card overflow-hidden">
        <table className="w-full [&_td]:text-xs [&_td]:py-2">
          <thead className="bg-ink-50 border-b border-ink-200">
            <tr>
              {modoSelecao && (
                <th className="w-8 px-2">
                  <input
                    type="checkbox"
                    checked={
                      filtrados.length > 0 &&
                      filtrados.filter(p => podeSelecionar(p)).every(p => selecionados.has(p.id))
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelecionados(new Set(filtrados.filter(p => podeSelecionar(p)).map(p => p.id)))
                      } else {
                        setSelecionados(new Set())
                      }
                    }}
                  />
                </th>
              )}
              <th onClick={() => toggleSort('numero')} className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2 cursor-pointer select-none hover:text-ink-900">#{arrow('numero')}</th>
              {!ocultar('cliente') && <th onClick={() => toggleSort('cliente')} className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2 cursor-pointer select-none hover:text-ink-900">Cliente / CPF{arrow('cliente')}</th>}
              {!ocultar('data') && <th onClick={() => toggleSort('data')} className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2 cursor-pointer select-none hover:text-ink-900">Data{arrow('data')}</th>}
              {!ocultar('tipo_venda') && <th onClick={() => toggleSort('tipo_venda')} className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2 cursor-pointer select-none hover:text-ink-900">Tipo{arrow('tipo_venda')}</th>}
              {!ocultar('rota') && <th onClick={() => toggleSort('rota')} className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2 cursor-pointer select-none hover:text-ink-900">Rota{arrow('rota')}</th>}
              {!ocultar('uf') && <th onClick={() => toggleSort('uf')} className="text-center text-xs font-semibold text-ink-600 uppercase px-3 py-2 cursor-pointer select-none hover:text-ink-900">UF{arrow('uf')}</th>}
              {!ocultar('bairro') && <th onClick={() => toggleSort('bairro')} className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2 cursor-pointer select-none hover:text-ink-900">Bairro{arrow('bairro')}</th>}
              {!ocultar('vendedor') && <th onClick={() => toggleSort('vendedor')} className="text-center text-xs font-semibold text-ink-600 uppercase px-3 py-2 cursor-pointer select-none hover:text-ink-900">Vend.{arrow('vendedor')}</th>}
              {!ocultar('canal') && <th onClick={() => toggleSort('canal')} className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2 cursor-pointer select-none hover:text-ink-900">Canal{arrow('canal')}</th>}
              {mostrarDeposito && !ocultar('deposito') && (
                <th onClick={() => toggleSort('deposito')} className="text-left text-xs font-semibold text-ink-600 uppercase px-3 py-2 cursor-pointer select-none hover:text-ink-900">Depósito{arrow('deposito')}</th>
              )}
              {!ocultar('total') && <th onClick={() => toggleSort('total')} className="text-right text-xs font-semibold text-ink-600 uppercase px-3 py-2 cursor-pointer select-none hover:text-ink-900">Total{arrow('total')}</th>}
              {!ocultar('status') && <th onClick={() => toggleSort('status')} className="text-center text-xs font-semibold text-ink-600 uppercase px-3 py-2 cursor-pointer select-none hover:text-ink-900">Status{arrow('status')}</th>}
              {!ocultar('tempo') && <th onClick={() => toggleSort('tempo')} className="text-center text-xs font-semibold text-ink-600 uppercase px-3 py-2 cursor-pointer select-none hover:text-ink-900">Tempo{arrow('tempo')}</th>}
              <th className="w-10 px-1">
                <ColunasMenu ocultas={colsOcultas} setOcultas={setColsOcultas} marketplaceAtivo={marketplaceAtivo} />
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {isLoading && (
              <tr><td colSpan={10} className="text-center py-8 text-ink-400 text-sm">Carregando...</td></tr>
            )}
            {!isLoading && filtrados.length === 0 && (
              <tr><td colSpan={10} className="text-center py-8 text-ink-400 text-sm">Nenhum pedido nesta visualização.</td></tr>
            )}
            {filtradosPagina.map(p => {
              const statusMeta = STATUS_META[p.status] || { label: p.status, cor: 'bg-gray-100 text-gray-700' }
              const canalMeta = CANAL_META[p.canal_origem] || { label: p.canal_origem, icon: Package2 }
              const CanalIcon = canalMeta.icon
              const dep = mapDeposito[p.deposito_id]

              return (
                <tr key={p.id}
                  onClick={() => {
                    if (modoSelecao) {
                      if (podeSelecionar(p)) toggleSelecao(p.id)
                    } else {
                      setPedidoAberto(p.id)
                    }
                  }}
                  className={`hover:bg-violet-50 cursor-pointer transition-colors ${
                    p.status === 'bloqueado' ? 'bg-rose-50' : ''
                  } ${modoSelecao && selecionados.has(p.id) ? 'bg-brand-50' : ''
                  } ${modoSelecao && !podeSelecionar(p) ? 'opacity-40' : ''}`}>
                  {modoSelecao && (
                    <td className="w-8 px-2" onClick={(e) => e.stopPropagation()}>
                      {podeSelecionar(p) ? (
                        <input
                          type="checkbox"
                          checked={selecionados.has(p.id)}
                          onChange={() => toggleSelecao(p.id)}
                        />
                      ) : null}
                    </td>
                  )}
                  <td className="px-3 py-2.5 font-medium text-sm">
                    #{p.numero}
                    {mostrarCanalTag && <CanalTag canal={p.caixa_canal} className="ml-1.5 align-middle" />}
                    {(() => {
                      if (!marketplaceAtivo) return null
                      const cl = classificarColeta(p)
                      if (!cl || cl === 'expedido' || ['entregue','cancelado'].includes(p.status)) return null
                      const meta = {
                        flex:     { label: '🔥 FLEX',    cor: 'bg-rose-100 text-rose-800 border-rose-300' },
                        hoje:     { label: 'HOJE',       cor: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
                        atrasado: { label: 'ATRASADO',   cor: 'bg-rose-200 text-rose-900 border-rose-400' },
                        agendado: { label: '📅 AGEND.',  cor: 'bg-sky-100 text-sky-800 border-sky-300' },
                      }[cl]
                      if (!meta) return null
                      return (
                        <div className={`inline-block ml-1.5 text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border ${meta.cor}`}>
                          {meta.label}
                        </div>
                      )
                    })()}
                  </td>
                  {!ocultar('cliente') && (
                    <td className="px-3 py-2.5 text-sm">
                      <div className="font-medium text-ink-900">
                        {p.cliente_nome || mapCliente[p.cliente_id] || <span className="text-ink-400">Cliente #{p.cliente_id}</span>}
                      </div>
                      {p.cliente_documento && (
                        <div className="text-[10px] text-ink-500 font-mono">{formatDoc(p.cliente_documento)}</div>
                      )}
                    </td>
                  )}
                  {!ocultar('data') && (
                    <td className="px-3 py-2.5 text-xs text-ink-600 whitespace-nowrap">
                      {p.criado_em ? new Date(p.criado_em).toLocaleDateString('pt-BR') : '—'}
                    </td>
                  )}
                  {!ocultar('tipo_venda') && (
                    <td className="px-3 py-2.5">
                      {p.tipo_venda === 'fiscal' ? (
                        <span className="text-[10px] px-2 py-0.5 rounded bg-indigo-100 text-indigo-700 font-medium">Fiscal</span>
                      ) : p.tipo_venda === 'simples' ? (
                        <span className="text-[10px] px-2 py-0.5 rounded bg-slate-200 text-slate-700 font-medium">Simples</span>
                      ) : <span className="text-ink-300 text-xs">—</span>}
                    </td>
                  )}
                  {!ocultar('rota') && (
                    <td className="px-3 py-2.5 text-xs text-ink-600">
                      {p.rota_nome || <span className="text-ink-300">—</span>}
                    </td>
                  )}
                  {!ocultar('uf') && (
                    <td className="px-3 py-2.5 text-xs text-center font-medium text-ink-700">
                      {p.cliente_uf || <span className="text-ink-300">—</span>}
                    </td>
                  )}
                  {!ocultar('bairro') && (
                    <td className="px-3 py-2.5 text-xs text-ink-600">
                      {p.cliente_bairro || <span className="text-ink-300">—</span>}
                    </td>
                  )}
                  {!ocultar('vendedor') && (
                    <td className="px-3 py-2.5 text-xs text-center font-medium text-ink-700">
                      {p.vendedor_codigo || <span className="text-ink-300">—</span>}
                    </td>
                  )}
                  {!ocultar('canal') && (
                    <td className="px-3 py-2.5">
                      <span className={`inline-flex items-center gap-1 text-xs ${p.canal_origem === 'celular' ? 'text-emerald-600 font-medium' : 'text-ink-600'}`}>
                        <CanalIcon size={12} /> {canalMeta.label}
                      </span>
                    </td>
                  )}
                  {mostrarDeposito && !ocultar('deposito') && (
                    <td className="px-3 py-2.5 text-xs text-ink-600">
                      {dep ? dep.codigo : '—'}
                    </td>
                  )}
                  {!ocultar('total') && (
                    <td className="px-3 py-2.5 text-right text-sm font-medium">
                      {formatMoney(valorLiquido(p))}
                      {/* 03/07: bike da troca = desconto; valor exibido é líquido, com a bike indicada */}
                      {!!p.bike_troca && p.bike_troca > 0 && (
                        <span className="block text-[10px] font-normal text-emerald-600"
                          title="Bike recebida na troca (desconto — virou estoque seminovo)">
                          🚲 −{formatMoney(p.bike_troca)}
                        </span>
                      )}
                    </td>
                  )}
                  {!ocultar('status') && (
                    <td className="px-3 py-2.5 text-center">
                      <span className={`inline-block text-[11px] font-semibold uppercase px-2 py-0.5 rounded ${statusMeta.cor}`}>
                        {statusMeta.label}
                      </span>
                    </td>
                  )}
                  {!ocultar('tempo') && (
                    <td className="px-3 py-2.5 text-center text-xs text-ink-500">
                      <span className="inline-flex items-center gap-1">
                        <Clock size={10} /> {tempoDesde(p.criado_em)}
                      </span>
                    </td>
                  )}
                  <td className="px-3 py-2.5 text-ink-400">
                    <div className="flex items-center justify-end gap-1">
                      {p.canal_origem === 'balcao' && p.status !== 'cancelado' && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setCupomPedido({ id: p.id, numero: p.numero }) }}
                          title="Reimprimir cupom do PDV"
                          className="p-1 rounded hover:bg-violet-100 text-violet-600 transition-colors">
                          <Printer size={14} />
                        </button>
                      )}
                      <ChevronRight size={14} />
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {/* Paginação (só da exibição — filtro/seleção/cards são sobre tudo) */}
        {filtradosSorted.length > PAGE_SIZE && (
          <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-ink-100 text-sm">
            <span className="text-ink-500">
              Mostrando <strong>{paginaSegura * PAGE_SIZE + 1}–{Math.min((paginaSegura + 1) * PAGE_SIZE, filtradosSorted.length)}</strong> de <strong>{filtradosSorted.length}</strong> pedidos
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPagina(0)} disabled={paginaSegura === 0}
                className="px-2 py-1 rounded border text-xs disabled:opacity-40">««</button>
              <button onClick={() => setPagina(p => Math.max(0, p - 1))} disabled={paginaSegura === 0}
                className="px-2 py-1 rounded border text-xs disabled:opacity-40">‹ Anterior</button>
              <span className="px-2 text-ink-600">Página {paginaSegura + 1} de {totalPaginas}</span>
              <button onClick={() => setPagina(p => Math.min(totalPaginas - 1, p + 1))} disabled={paginaSegura >= totalPaginas - 1}
                className="px-2 py-1 rounded border text-xs disabled:opacity-40">Próxima ›</button>
              <button onClick={() => setPagina(totalPaginas - 1)} disabled={paginaSegura >= totalPaginas - 1}
                className="px-2 py-1 rounded border text-xs disabled:opacity-40">»»</button>
            </div>
          </div>
        )}
      </div>

      {/* Drawer */}
      {/* Barra flutuante modo lote */}
      {modoSelecao && selecionados.size > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-white border border-ink-300 shadow-lg rounded-lg px-4 py-3 flex items-center gap-3 z-40">
          <div className="flex flex-col leading-tight pr-1 border-r border-ink-200 mr-1">
            <span className="text-sm font-medium">{selecionados.size} pedido(s)</span>
            <span className="text-xs text-ink-600">
              <span className="font-semibold text-emerald-700">{formatMoney(totaisSelecao?.valor_total ?? 0)}</span>
              {' · '}
              <span className="font-semibold text-ink-700">{(totaisSelecao?.peso_total_kg ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kg</span>
              {totaisSelecao?.sem_peso > 0 && (
                <span className="text-amber-600" title="Itens sem peso cadastrado não entram no total"> ⚠ {totaisSelecao.sem_peso} s/ peso</span>
              )}
            </span>
          </div>
          <button
            onClick={gerarCargaSelecionados}
            disabled={!!loteProgresso}
            className="btn-primary text-sm disabled:opacity-50"
            title="Monta uma carga de distribuição com os selecionados (faturar em lote é feito na tela da carga)"
          >
            <Truck size={14} /> Gerar carga
          </button>
          <button
            onClick={() => executarLote('preview')}
            disabled={!!loteProgresso}
            className="btn-ghost text-sm disabled:opacity-50"
            title="Roda preview XML em todos e mostra alertas consolidados"
          >
            🧪 Preview em lote
          </button>
          <button
            onClick={() => executarLote('danfe')}
            disabled={!!loteProgresso}
            className="btn-ghost text-sm disabled:opacity-50"
            title="Abre DANFE A4 de todos em abas novas"
          >
            <Printer size={14} /> DANFEs (A4)
          </button>
          <button
            onClick={() => executarLote('danfe-simples')}
            disabled={!!loteProgresso}
            className="btn-ghost text-sm disabled:opacity-50"
            title="Abre DANFE simplificado"
          >
            <Printer size={14} /> DANFEs (simples)
          </button>
          <button onClick={limparSelecao} disabled={!!loteProgresso} className="text-ink-500 hover:text-red-600">
            <XIcon size={16} />
          </button>
        </div>
      )}

      {/* Progresso */}
      {loteProgresso && (
        <div className="fixed bottom-4 right-4 bg-brand-600 text-white px-4 py-2 rounded-lg shadow-lg z-50 text-sm">
          <Loader2 className="inline animate-spin mr-2" size={14} /> {loteProgresso}
        </div>
      )}

      {/* Modal resultado preview lote */}
      {loteResultado && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[85vh] overflow-y-auto">
            <div className="border-b border-ink-200 p-4 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-lg font-semibold">Resultado do preview em lote</h2>
              <button onClick={() => { setLoteResultado(null); limparSelecao() }}>
                <XIcon size={18} />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div className="text-sm">
                <strong className="text-emerald-700">{loteResultado.filter((r: any) => r.ok && (!r.alertas || r.alertas.length === 0)).length}</strong> sem problemas |{' '}
                <strong className="text-amber-700">{loteResultado.filter((r: any) => r.ok && r.alertas && r.alertas.length > 0).length}</strong> com alertas |{' '}
                <strong className="text-red-700">{loteResultado.filter((r: any) => !r.ok).length}</strong> com erro
              </div>
              {loteResultado.map((r: any) => (
                <div key={r.pid} className={`border rounded p-2 text-xs ${
                  !r.ok ? 'bg-red-50 border-red-200' :
                  r.alertas && r.alertas.length > 0 ? 'bg-amber-50 border-amber-200' :
                  'bg-emerald-50 border-emerald-200'
                }`}>
                  <div className="font-semibold">Pedido #{r.pid}</div>
                  {r.erro && <div className="text-red-700">Erro: {r.erro}</div>}
                  {r.chave && <div className="font-mono text-[10px] text-ink-500">...{r.chave.slice(-12)}</div>}
                  {r.alertas && r.alertas.length > 0 && (
                    <ul className="mt-1 ml-4 list-disc text-amber-800">
                      {r.alertas.map((a: string, i: number) => <li key={i}>{a}</li>)}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {pedidoAberto && (
        <PedidoDrawer
          pedidoId={pedidoAberto}
          onClose={() => setPedidoAberto(null)}
          mapCliente={mapCliente}
          mapDeposito={mapDeposito}
        />
      )}

      {cupomPedido && (
        <PreviewCupomModal
          pedidoId={cupomPedido.id}
          numero={cupomPedido.numero}
          onClose={() => setCupomPedido(null)}
        />
      )}

      {relatorio && (
        <PreviewRelatorioModal
          ids={relatorio.ids}
          titulo={relatorio.titulo}
          onClose={() => setRelatorio(null)}
        />
      )}
    </div>
  )
}


// =============================================================
// CHIP DE FILTRO
// =============================================================
function FiltroChip({ label, ativo, cor, onClick }: {
  label: string; ativo: boolean; cor?: string; onClick: () => void
}) {
  return (
    <button onClick={onClick}
      className={`text-xs font-medium px-3 py-1.5 rounded-full transition-colors
        ${ativo
          ? 'bg-brand-600 text-white'
          : cor ? `${cor} hover:opacity-80` : 'bg-ink-100 text-ink-700 hover:bg-ink-200'
        }`}>
      {label}
    </button>
  )
}


// =============================================================
// PAINEL DE BLOQUEIO — mostra crédito do cliente (limite/dívida) e desbloqueio
// =============================================================
function BloqueioPanel({ pedido, onDesbloquear, desbloqueando }: { pedido: any; onDesbloquear: () => void; desbloqueando: boolean }) {
  const { data: dossie } = useQuery<any>({
    queryKey: ['credito-dossie', pedido.cliente_id],
    queryFn: async () => (await api.get(`/financeiro/analise-credito/${pedido.cliente_id}`)).data,
    enabled: !!pedido.cliente_id,
  })
  const veioWinThor = (pedido.observacao || '').includes('WinThor pos=B')
  const limite = dossie?.limite
  const saldo = dossie?.saldo
  const disp = dossie?.disponivel
  const pct = dossie?.pct_usado
  const titulos: any[] = dossie?.titulos_em_aberto || []
  const atrasados = titulos.filter((t) => t.atrasado)
  const totalAtraso = atrasados.reduce((s, t) => s + (t.saldo || 0), 0)
  const estourou = limite != null && saldo != null && saldo > limite
  return (
    <div className="rounded-lg border-2 border-rose-300 bg-rose-50 p-4 space-y-3">
      <div className="flex items-center gap-2 text-rose-800 font-semibold">🔒 Pedido bloqueado</div>
      {veioWinThor && <p className="text-xs text-rose-700">Importado <strong>bloqueado do WinThor</strong>. Confira o crédito do cliente antes de liberar.</p>}
      {dossie ? (
        <div className="space-y-1.5">
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div><div className="text-ink-500 uppercase">Limite</div><div className="font-semibold">{limite != null ? formatMoney(limite) : 'sem limite'}</div></div>
            <div><div className="text-ink-500 uppercase">Em aberto</div><div className="font-semibold">{formatMoney(saldo || 0)}</div></div>
            <div><div className="text-ink-500 uppercase">Disponível</div><div className={`font-semibold ${estourou ? 'text-rose-700' : 'text-emerald-700'}`}>{disp != null ? formatMoney(disp) : '—'}{pct != null ? ` · ${pct}%` : ''}</div></div>
          </div>
          {atrasados.length > 0 ? (
            <div className="text-xs text-rose-800 font-medium bg-rose-100 rounded px-2 py-1">⚠️ {atrasados.length} título(s) em atraso — <strong>{formatMoney(totalAtraso)}</strong> de dívida vencida</div>
          ) : (
            <div className="text-xs text-emerald-700">✓ Sem títulos em atraso {(!saldo || saldo === 0) ? '— sem débitos no momento (bloqueio veio do WinThor)' : ''}</div>
          )}
          {estourou && <div className="text-xs text-rose-800 font-medium">⚠️ Saldo devedor acima do limite de crédito.</div>}
        </div>
      ) : <div className="text-xs text-ink-400">Carregando crédito do cliente…</div>}
      <button onClick={onDesbloquear} disabled={desbloqueando}
        className="w-full px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
        🔓 {desbloqueando ? 'Desbloqueando…' : 'Desbloquear pedido'}
      </button>
      <p className="text-[11px] text-ink-500 text-center">Desbloquear libera o pedido pro fluxo (separação/faturamento). Confira a dívida acima antes.</p>
    </div>
  )
}

// =============================================================
// DRAWER DE DETALHE
// =============================================================
function PedidoDrawer({ pedidoId, onClose, mapCliente, mapDeposito }: {
  pedidoId: number
  onClose: () => void
  mapCliente: Record<number, string>
  mapDeposito: Record<number, any>
}) {
  const qc = useQueryClient()
  const nav = useNavigate()

  const { data: pedido, isLoading } = useQuery<Pedido>({
    queryKey: ['pedido', pedidoId],
    queryFn: async () => (await api.get(`/vendas/${pedidoId}`)).data,
  })

  const { data: eventos = [] } = useQuery<any[]>({
    queryKey: ['pedido-eventos', pedidoId],
    queryFn: async () => {
      try {
        return (await api.get(`/vendas/pedidos/${pedidoId}/eventos`)).data
      } catch { return [] }
    },
    enabled: !!pedido,
  })

  // FEATURE 11/08: nota/cupom autorizado deste pedido → habilita o botão de CANCELAR
  // na SEFAZ. modelo 55 = NF-e ("nota"), modelo 65 = NFC-e ("cupom"). O texto do botão
  // segue o modelo emitido, que é o que o operador enxerga no balcão.
  const { data: emissaoAutorizada } = useQuery<any | null>({
    queryKey: ['pedido-emissao-autorizada', pedidoId],
    queryFn: async () => {
      try {
        const r = await api.get('/nfe-proprio/emissoes', {
          params: { pedido_id: pedidoId, status: 'autorizada', limit: 5 },
        })
        return (r.data?.emissoes || [])[0] || null
      } catch { return null }
    },
    enabled: !!pedido,
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['pedido', pedidoId] })
    qc.invalidateQueries({ queryKey: ['pedido-eventos', pedidoId] })
    qc.invalidateQueries({ queryKey: ['pedido-emissao-autorizada', pedidoId] })
    qc.invalidateQueries({ queryKey: ['pedidos'] })
  }

  const ehCupom = emissaoAutorizada?.modelo === '65'
  const rotuloDoc = ehCupom ? 'cupom fiscal' : 'nota fiscal'

  // Cancelamento fiscal DE VERDADE: vai pra SEFAZ (assina + transmite o evento) via
  // emissor próprio. NÃO usa o provider do agregador — sem token ele é MockProvider e
  // marcaria cancelado só no sistema, deixando a nota VIVA na SEFAZ.
  const cancelarFiscal = useMutation({
    mutationFn: async (justificativa: string) =>
      (await api.post(`/nfe-proprio/cancelar/${emissaoAutorizada?.id}`, { justificativa },
        { timeout: 120000 })).data,
    onSuccess: (r: any) => {
      if (r?.sucesso) {
        toast.success(`${ehCupom ? 'Cupom' : 'Nota'} cancelado na SEFAZ! ${r.protocolo ? `Protocolo ${r.protocolo}` : ''}`,
          { duration: 6000 })
        invalidate()
      } else {
        toast.error(`SEFAZ não cancelou: ${r?.xMotivo || r?.erro || 'rejeitado'}`, { duration: 8000 })
      }
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao cancelar na SEFAZ', { duration: 8000 }),
  })

  // Pede a justificativa (SEFAZ exige 15-255 chars) e só então o modal de senha de
  // admin/gerente (mesmo gate das exclusões) — o dono pediu que operador consiga
  // cancelar sem precisar que o admin faça login.
  const pedirCancelamentoFiscal = () => {
    const just = window.prompt(
      `Cancelar ${rotuloDoc} nº ${emissaoAutorizada?.numero} na SEFAZ.\n\n` +
      'Justificativa (mínimo 15 caracteres, exigência da SEFAZ):', '')
    if (just === null) return
    const j = just.trim()
    if (j.length < 15) { toast.error('A justificativa precisa ter no mínimo 15 caracteres.'); return }
    if (j.length > 255) { toast.error('A justificativa passa de 255 caracteres.'); return }
    pedirExclusao({
      titulo: `Cancelar ${rotuloDoc} na SEFAZ`,
      descricao: `o cancelamento d${ehCupom ? 'o cupom' : 'a nota'} nº ${emissaoAutorizada?.numero} na SEFAZ (ação irreversível)`,
      confirmLabel: 'Cancelar na SEFAZ',
      onConfirm: () => cancelarFiscal.mutate(j),
    })
  }

  // Acoes do fluxo
  const { user } = useAuth()
  const isAdmin = user?.perfil === 'admin' || user?.perfil === 'super_admin'
  // Editar pedido também liberado p/ gerente e financeiro (backend aceita os mesmos perfis)
  const podeEditar = isAdmin || user?.perfil === 'gerente' || user?.perfil === 'financeiro'
  // Modal de excecao de credito (CRED-7b)
  const [excecaoModal, setExcecaoModal] = useState<null | { detalhe: any; retry: (autorizar: boolean, just: string) => void }>(null)
  // Modal cancelar/devolver venda (fiscal-aware)
  const [showCancelar, setShowCancelar] = useState(false)
  const [showCupom, setShowCupom] = useState(false)
  // Modal editar pedido OMS (admin/gerente, pré-fluxo)
  const [showEditarOMS, setShowEditarOMS] = useState(false)
  // FEATURE 03/07: editar venda já feita (observação livre; cliente/vendedor c/ senha) — qualquer status.
  const [showEditarVenda, setShowEditarVenda] = useState(false)
  const [itensAbertos, setItensAbertos] = useState(false)

  // Helper: extrai 422 LIMITE_CREDITO_EXCEDIDO e abre modal. Retorna true se tratou.
  function tratarErroCredito(err: any, retry: (autorizar: boolean, just: string) => void): boolean {
    const detail = err?.response?.data?.detail
    if (err?.response?.status === 422 && detail && typeof detail === 'object' && detail.code === 'LIMITE_CREDITO_EXCEDIDO') {
      setExcecaoModal({ detalhe: detail, retry })
      return true
    }
    return false
  }

  // Helper: extrai mensagem de erro amigavel (trata detail object)
  function msgErro(err: any): string {
    const detail = err?.response?.data?.detail
    if (typeof detail === 'string') return detail
    if (typeof detail === 'object' && detail?.message) return detail.message
    return 'Erro'
  }

  const aprovar = useMutation({
    mutationFn: (body: any = { observacao: 'Pagamento OK' }) =>
      api.post(`/vendas/pedidos/${pedidoId}/aprovar`, body),
    onSuccess: () => { toast.success('Pedido aprovado'); invalidate() },
    onError: (e: any) => {
      const tratado = tratarErroCredito(e, (autorizar, just) =>
        aprovar.mutate({ observacao: 'Pagamento OK', autorizar_excecao: autorizar, justificativa_excecao: just })
      )
      if (!tratado) toast.error(msgErro(e))
    },
  })
  const reprovar = useMutation({
    mutationFn: (motivo: string) => api.post(`/vendas/pedidos/${pedidoId}/reprovar-pagamento`, { motivo }),
    onSuccess: () => { toast.success('Pedido reprovado'); invalidate() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  // FEATURE 30/06: pagamento modular, desacoplado do "aprovar". Confirmar/desfazer
  // funcionam em qualquer etapa (independem do status OMS). Pré-faturamento: só flag.
  const confirmarPag = useMutation({
    mutationFn: (obs?: string) => api.post(`/vendas/pedidos/${pedidoId}/confirmar-pagamento`, { observacao: obs }),
    onSuccess: () => { toast.success('Pagamento confirmado'); invalidate() },
    onError: (e: any) => toast.error(msgErro(e)),
  })
  const desfazerPag = useMutation({
    mutationFn: (obs?: string) => api.post(`/vendas/pedidos/${pedidoId}/desfazer-pagamento`, { observacao: obs }),
    onSuccess: () => { toast.success('Confirmação de pagamento desfeita'); invalidate() },
    onError: (e: any) => toast.error(msgErro(e)),
  })
  const setMomento = useMutation({
    mutationFn: (m: string) => api.post(`/vendas/pedidos/${pedidoId}/momento-pagamento`, { momento: m }),
    onSuccess: () => { invalidate() },
    onError: (e: any) => toast.error(msgErro(e)),
  })
  // FEATURE 30/06: pipeline modular — avançar pulando etapas desligadas; e definir etapas.
  const avancar = useMutation({
    mutationFn: (body: any = {}) => api.post(`/vendas/pedidos/${pedidoId}/avancar`, body),
    onSuccess: () => { toast.success('Etapa avançada'); invalidate() },
    onError: (e: any) => {
      const tratado = tratarErroCredito(e, (autorizar, just) =>
        avancar.mutate({ autorizar_excecao: autorizar, justificativa_excecao: just }))
      if (!tratado) toast.error(msgErro(e))
    },
  })
  const setEtapas = useMutation({
    mutationFn: (patch: Record<string, boolean>) => api.post(`/vendas/pedidos/${pedidoId}/etapas`, patch),
    onSuccess: () => { invalidate() },
    onError: (e: any) => toast.error(msgErro(e)),
  })
  const iniciarSep = useMutation({
    mutationFn: () => api.post(`/vendas/pedidos/${pedidoId}/iniciar-separacao`, {}),
    onSuccess: () => { toast.success('Separação iniciada'); invalidate() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  const pausarSep = useMutation({
    mutationFn: (obs: string) => api.post(`/vendas/pedidos/${pedidoId}/pausar-separacao`, { observacao: obs }),
    onSuccess: () => { toast.success('Separação pausada'); invalidate() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  const concluirSep = useMutation({
    mutationFn: () => api.post(`/vendas/pedidos/${pedidoId}/concluir-separacao`, {}),
    onSuccess: () => { toast.success('Separação concluída'); invalidate() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  const concluirEmb = useMutation({
    mutationFn: () => api.post(`/vendas/pedidos/${pedidoId}/concluir-embalagem`, {}),
    onSuccess: () => { toast.success('Embalagem concluída — aguardando NF'); invalidate() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  const marcarFat = useMutation({
    mutationFn: (body: any = {}) =>
      api.post(`/vendas/pedidos/${pedidoId}/marcar-faturado`, body),
    // FEATURE 30/06: faturar SEM nota → abre o cupom/recibo (80mm) pra impressão,
    // já que não há DANFE. Antes não aparecia nada depois de faturar.
    onSuccess: () => { toast.success('Pedido faturado — financeiro e estoque atualizados'); invalidate(); setShowCupom(true) },
    onError: (e: any) => {
      const tratado = tratarErroCredito(e, (autorizar, just) =>
        marcarFat.mutate({ autorizar_excecao: autorizar, justificativa_excecao: just })
      )
      if (!tratado) toast.error(msgErro(e))
    },
  })
  // ============================================================
  // NFe propria — preview (sem emissao real; depende de certificado)
  // ============================================================

  const carregarPreview = useMutation({
    mutationFn: async (pid: number) => {
      const r = await api.get(`/nfe-proprio/preview-xml/${pid}`)
      return { data: r.data, pedidoId: pid }
    },
    onSuccess: ({ data, pedidoId }) => {
      const alertas: string[] = data?.alertas || []
      if (alertas.length > 0) {
        toast.error(`${alertas.length} alerta(s) fiscal(is):\n${alertas.slice(0, 5).join('\n')}`, { duration: 7000 })
      } else {
        toast.success(`NFe preview OK! Chave: ${data.chave_acesso?.slice(-8)}`, { duration: 4000 })
      }
      // Abre DANFE A4 em nova aba
      void baixarDanfe(pedidoId, false)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao gerar preview'),
  })

  const baixarDanfe = async (pid: number, simples: boolean) => {
    try {
      const r = await api.get(`/nfe-proprio/danfe/${pid}${simples ? '?simples=true' : ''}`, {
        responseType: 'blob',
      })
      const blob = new Blob([r.data], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)
      window.open(url, '_blank')
      setTimeout(() => URL.revokeObjectURL(url), 30000)
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro ao gerar DANFE')
    }
  }

  const emitirNF = useMutation({
    mutationFn: async (tipo: string) => {
      // NF-e modelo 55: emissor proprio REAL (assina + transmite + autoriza na SEFAZ).
      if (tipo === 'NFe') return (await api.post(`/nfe-proprio/emitir/${pedidoId}`, null, { timeout: 120000 })).data
      // NFC-e (65) e demais seguem o caminho fiscal/provider existente.
      return (await api.post('/fiscal/emitir', { pedido_id: pedidoId, tipo })).data
    },
    onSuccess: (doc: any) => {
      const autorizada = doc.autorizada === true || doc.status === 'autorizado'
      const rejeitada = doc.autorizada === false || doc.status === 'rejeitado'
      if (autorizada) toast.success(`NF-e ${doc.numero || ''} autorizada!${doc.protocolo ? ` Protocolo ${doc.protocolo}` : ''}`)
      else if (rejeitada) toast.error(`NF-e rejeitada: ${doc.xMotivo || doc.erro || doc.mensagem || 'erro'}`, { duration: 8000 })
      else toast.success('NF em processamento...')
      invalidate()
    },
    onError: (e: any) => {
      const d = e?.response?.data?.detail
      if (d && typeof d === 'object' && d.pendencias) {
        toast.error(`${d.pendencias.length} pendências fiscais. Abra o console p/ detalhes.`, { duration: 6000 })
        console.error('Pendências:', d.pendencias)
      } else toast.error(d?.mensagem || d || 'Erro ao emitir NF')
    },
  })
  // Gerar nota fiscal DEPOIS de faturado simples (cliente pediu nota): só fiscal,
  // sem mexer em estoque/financeiro (já lançados no faturamento).
  const gerarNota = useMutation({
    mutationFn: async () => (await api.post(`/vendas/pedidos/${pedidoId}/gerar-nota`, null, { timeout: 120000 })).data,
    onSuccess: (doc: any) => {
      if (doc?.autorizada) toast.success(`NF-e ${doc.numero || ''} autorizada!${doc.protocolo ? ` Protocolo ${doc.protocolo}` : ''}`, { duration: 6000 })
      else toast.success('NF em processamento…')
      invalidate()
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao gerar nota'),
  })
  const enviarWhats = useMutation({
    mutationFn: async () => {
      const tel = window.prompt('WhatsApp do cliente (deixe em branco pra usar o do cadastro):', '')
      if (tel === null) throw new Error('cancelado')
      return (await api.post(`/pdv/venda/${pedidoId}/enviar-whatsapp`,
        { tipo: 'cupom', telefone: tel.trim() || undefined })).data
    },
    onSuccess: (r: any) => toast.success(`Comprovante enviado para ${r.enviado_para}`),
    onError: (e: any) => {
      if (e?.message === 'cancelado') return
      toast.error(e?.response?.data?.detail || 'Erro ao enviar WhatsApp')
    },
  })

  // Gera (se preciso) e abre o boleto do pedido (forma=boleto)
  const [boletoLoading, setBoletoLoading] = useState(false)
  const verBoleto = async () => {
    setBoletoLoading(true)
    try {
      const r = await api.get(`/financeiro/boletos/por-pedido/${pedidoId}`)
      const parcelas = r.data?.parcelas || []
      if (!parcelas.length) { toast.error('Sem conta a receber de boleto pra este pedido'); return }
      const c = parcelas[0]
      if (!c.ja_gerado) {
        const g = await api.post('/financeiro/boletos/gerar', { conta_ids: [c.conta_id] })
        const res0 = (g.data?.resultados || [])[0]
        if (!res0?.ok) { toast.error(res0?.erro || 'Falha ao gerar boleto (banco emissor configurado?)'); return }
      }
      const pdf = await api.get(`/financeiro/boletos/${c.conta_id}/pdf`, { responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([pdf.data], { type: 'application/pdf' }))
      window.open(url, '_blank'); setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch (e: any) { toast.error(e?.response?.data?.detail || 'Erro no boleto') }
    finally { setBoletoLoading(false) }
  }
  const expedir = useMutation({
    mutationFn: (d: { transportadora: string; rastreio_codigo: string }) =>
      api.post(`/vendas/pedidos/${pedidoId}/expedir`, d),
    onSuccess: () => { toast.success('Pedido expedido'); invalidate() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  const entregue = useMutation({
    mutationFn: () => api.post(`/vendas/pedidos/${pedidoId}/marcar-entregue`, {}),
    onSuccess: () => { toast.success('Pedido entregue'); invalidate() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  const excluir = useMutation({
    mutationFn: () => api.delete(`/vendas/${pedidoId}`),
    onSuccess: () => { toast.success('Pedido excluído — pode reimportar'); invalidate(); onClose() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao excluir'),
  })

  const reabrirEmb = useMutation({
    mutationFn: (m: string) => api.post(`/vendas/pedidos/${pedidoId}/reabrir-embalagem`, { motivo: m }),
    onSuccess: () => { toast.success('Pedido voltou para embalagem'); invalidate() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  const reabrirSep = useMutation({
    mutationFn: (m: string) => api.post(`/vendas/pedidos/${pedidoId}/reabrir-separacao`, { motivo: m }),
    onSuccess: () => { toast.success('Pedido voltou para separação'); invalidate() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  const reabrirPag = useMutation({
    mutationFn: (m: string) => api.post(`/vendas/pedidos/${pedidoId}/reabrir-pagamento`, { motivo: m }),
    onSuccess: () => { toast.success('Pedido voltou para aberto'); invalidate() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })
  const finalizarDireto = useMutation({
    mutationFn: (body: any = {}) =>
      api.post(`/vendas/pedidos/${pedidoId}/finalizar-direto`, body, { timeout: 120000 }),
    onSuccess: (resp: any, variables: any) => {
      toast.success(resp?.data?.mensagem || 'Venda faturada — NF-e emitida, estoque baixado e a receber gerado', { duration: 6000 })
      invalidate()
      // FEATURE 30/06: faturou SEM nota (emitir_nfe=false) → abre cupom/recibo 80mm
      // pra impressão (não há DANFE). Com NF-e, o usuário imprime a DANFE.
      if (variables?.emitir_nfe === false) setShowCupom(true)
    },
    onError: (e: any) => {
      const tratado = tratarErroCredito(e, (autorizar, just) =>
        finalizarDireto.mutate({ autorizar_excecao: autorizar, justificativa_excecao: just })
      )
      if (!tratado) toast.error(msgErro(e))
    },
  })
  const bloquear = useMutation({
    mutationFn: (motivo?: string) => api.post(`/vendas/pedidos/${pedidoId}/bloquear`, { observacao: motivo || null }),
    onSuccess: () => { toast.success('Pedido bloqueado'); invalidate() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao bloquear'),
  })
  const desbloquear = useMutation({
    mutationFn: () => api.post(`/vendas/pedidos/${pedidoId}/desbloquear`),
    onSuccess: () => { toast.success('Pedido desbloqueado'); invalidate() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao desbloquear'),
  })
  const isSimplificado = useIsSimplificado()

  if (!pedido) return null
  const statusMeta = STATUS_META[pedido.status] || { label: pedido.status, cor: 'bg-gray-100 text-gray-700' }
  const canalMeta = CANAL_META[pedido.canal_origem] || { label: pedido.canal_origem, icon: Package2 }
  const dep = mapDeposito[pedido.deposito_id]
  const isPDV = pedido.canal_origem === 'balcao'

  // FEATURE 30/06: pipeline MODULAR por pedido. As etapas opcionais (aprovação,
  // separação, embalagem) podem estar desligadas em pedido.etapas → as ações dessas
  // etapas somem e o "avançar" pula direto pra próxima ativa. etapas ausente = todas
  // ativas = comportamento de antes.
  const etapas = {
    aprovacao: pedido.etapas?.aprovacao ?? true,
    separacao: pedido.etapas?.separacao ?? true,
    embalagem: pedido.etapas?.embalagem ?? true,
    expedicao: pedido.etapas?.expedicao ?? true,
  }
  // Próxima etapa ATIVA da espinha pré-faturamento (rótulo do botão "Avançar").
  const _SPINE_PRE = ['aberto', 'aprovado', 'separacao', 'embalagem', 'aguardando_nf']
  const _STAGE_FLAG: Record<string, keyof typeof etapas | undefined> = { aprovado: 'aprovacao', separacao: 'separacao', embalagem: 'embalagem' }
  const _STAGE_LABEL: Record<string, string> = { aprovado: 'Aprovado', separacao: 'Separação', embalagem: 'Embalagem', aguardando_nf: 'Faturamento' }
  const proximaEtapa = (() => {
    const i = _SPINE_PRE.indexOf(pedido.status)
    if (i < 0) return null
    for (const s of _SPINE_PRE.slice(i + 1)) {
      const fl = _STAGE_FLAG[s]
      if (!fl || etapas[fl]) return s
    }
    return null
  })()
  const acoesDisponiveis = isPDV ? [] : (() => {
    switch (pedido.status) {
      case 'aberto': return etapas.aprovacao ? ['aprovar', 'reprovar'] : ['avancar', 'reprovar']
      case 'aprovado': return [etapas.separacao ? 'iniciar-sep' : 'avancar', 'reabrir-pag', 'cancelar']
      case 'separacao': return ['concluir-sep', 'pausar-sep', 'cancelar']  // concluir pula embalagem se off (backend)
      case 'embalagem': return ['abrir-embalagem', 'concluir-emb', 'reabrir-sep', 'cancelar']
      case 'aguardando_nf': return ['emitir-nf', 'marcar-faturado', 'reabrir-emb', 'cancelar']
      case 'faturado': return ['expedir']
      case 'expedido': return ['entregue']
      default: return []
    }
  })()

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 bottom-0 w-full max-w-xl bg-white z-50 shadow-2xl overflow-y-auto">
        <div className="sticky top-0 z-10 bg-white border-b px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="p-1.5 rounded hover:bg-ink-100">
              <XIcon size={18} />
            </button>
            <h2 className="font-semibold text-lg">Pedido #{pedido.numero}</h2>
            <span className={`inline-block text-[11px] font-semibold uppercase px-2 py-0.5 rounded ${statusMeta.cor}`}>
              {statusMeta.label}
            </span>
            {/* FEATURE 30/06: chip de pagamento (independe do status do fluxo) */}
            {!isPDV && !['cancelado', 'devolvido'].includes(pedido.status) && (
              pedido.pagamento_confirmado_em ? (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded bg-emerald-100 text-emerald-800">
                  <CheckCircle2 size={11} /> Pago
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-800">
                  <Clock size={11} /> A receber
                </span>
              )
            )}
            {!isPDV && pedido.status === 'bloqueado' && (
              <button onClick={() => desbloquear.mutate()} disabled={desbloquear.isPending}
                className="text-[11px] font-semibold px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 hover:bg-emerald-200">
                🔓 Desbloquear
              </button>
            )}
            {!isPDV && !['bloqueado','cancelado','devolvido','entregue','faturado'].includes(pedido.status) && (
              <button onClick={() => { const m = prompt('Motivo do bloqueio (opcional):'); if (m !== null) bloquear.mutate(m) }}
                disabled={bloquear.isPending}
                className="text-[11px] font-semibold px-2 py-0.5 rounded bg-rose-100 text-rose-700 hover:bg-rose-200">
                🔒 Bloquear
              </button>
            )}
          </div>
          <canalMeta.icon size={16} className="text-ink-400" />
        </div>

        <div className="p-5 space-y-5">
          {isLoading ? (
            <div className="text-center text-ink-400 py-10">Carregando...</div>
          ) : (
            <>
              {pedido.status === 'bloqueado' && (
                <BloqueioPanel pedido={pedido} desbloqueando={desbloquear.isPending}
                  onDesbloquear={() => desbloquear.mutate()} />
              )}
              {/* Dados gerais */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs text-ink-500 uppercase">Cliente</div>
                  <div className="font-medium">{mapCliente[pedido.cliente_id] || `#${pedido.cliente_id}`}</div>
                </div>
                <div>
                  <div className="text-xs text-ink-500 uppercase">Canal</div>
                  <div>{canalMeta.label}</div>
                </div>
                <div>
                  <div className="text-xs text-ink-500 uppercase flex items-center gap-1">
                    <Warehouse size={10} /> Depósito
                  </div>
                  <div>{dep ? `${dep.nome} (${dep.codigo})` : '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-ink-500 uppercase">Pagamento</div>
                  <div className="capitalize">{pedido.forma_pagamento.replace('_', ' ')} — {pedido.parcelas}x</div>
                </div>
                <div>
                  <div className="text-xs text-ink-500 uppercase">Tipo de venda</div>
                  <div>
                    {pedido.tipo_venda === 'fiscal' ? (
                      <span className="text-[11px] px-2 py-0.5 rounded bg-indigo-100 text-indigo-700 font-medium">Com nota (NF-e)</span>
                    ) : pedido.tipo_venda === 'simples' ? (
                      <span className="text-[11px] px-2 py-0.5 rounded bg-slate-200 text-slate-700 font-medium">Sem nota</span>
                    ) : <span className="text-ink-400 text-sm">— não definido</span>}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-ink-500 uppercase">Total</div>
                  <div className="text-lg font-semibold text-brand-600">{formatMoney(pedido.total)}</div>
                </div>
                {Number(pedido.valor_st) > 0 && (
                  <div>
                    <div className="text-xs text-ink-500 uppercase">ICMS-ST / DIFAL</div>
                    <div className="text-base font-semibold text-amber-600">{formatMoney(pedido.valor_st)}</div>
                    <div className="text-[10px] text-ink-400">a recolher (GNRE) — vai na nota</div>
                  </div>
                )}
                {pedido.rastreio_codigo && (
                  <div>
                    <div className="text-xs text-ink-500 uppercase">Rastreio</div>
                    <div className="font-mono text-xs">
                      {pedido.transportadora} — {pedido.rastreio_codigo}
                    </div>
                  </div>
                )}
              </div>

              {/* Itens — escondidos por padrão; clica pra expandir (fonte menor) */}
              <div>
                <button onClick={() => setItensAbertos(o => !o)}
                  className="w-full flex items-center justify-between text-xs font-semibold text-ink-500 uppercase mb-2 hover:text-ink-700">
                  <span>Itens ({pedido.itens.length})</span>
                  <span className="text-ink-400 normal-case font-normal">{itensAbertos ? '▲ ocultar' : '▼ ver itens'}</span>
                </button>
                {itensAbertos && (
                  <div className="border border-ink-200 rounded-lg divide-y divide-ink-100">
                    {pedido.itens.map((it: any) => (
                      <div key={it.id} className="px-3 py-1.5 flex items-center justify-between text-xs">
                        <div>
                          <div className="font-medium">{it.descricao}</div>
                          <div className="text-[11px] text-ink-500">
                            {it.quantidade} × {formatMoney(it.preco_unitario)}
                            {it.desconto > 0 && ` − ${formatMoney(it.desconto)} desc`}
                          </div>
                        </div>
                        <div className="font-medium">{formatMoney(it.total)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Nota fiscal (pedido ja faturado): se TEM nota → DANFE/XML; se foi
                  faturado SEM nota → botão p/ gerar a NF-e depois (cliente pediu) */}
              {['faturado', 'entregue', 'expedido', 'confirmado'].includes(pedido.status) && pedido.status !== 'cancelado' && (
                <div>
                  <div className="text-xs font-semibold text-ink-500 uppercase mb-2">Nota Fiscal</div>
                  {pedido.tipo_venda === 'fiscal' ? (
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={async () => {
                          try {
                            const r = await api.get(`/nfe-proprio/danfe-autorizado/${pedidoId}`, { responseType: 'blob' })
                            const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }))
                            window.open(url, '_blank')
                            setTimeout(() => URL.revokeObjectURL(url), 60000)
                          } catch (e: any) {
                            toast.error(e?.response?.status === 404 ? 'Pedido sem NF-e autorizada' : 'Erro ao gerar DANFE')
                          }
                        }}
                        className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-brand-50 hover:bg-brand-100 text-brand-700 text-sm font-medium">
                        <Printer size={16} /> Imprimir DANFE
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            const r = await api.get(`/nfe-proprio/xml-autorizado/${pedidoId}`, { responseType: 'blob' })
                            const url = URL.createObjectURL(new Blob([r.data], { type: 'application/xml' }))
                            const a = document.createElement('a')
                            a.href = url; a.download = `nfe-${pedido.numero}.xml`; a.click()
                            setTimeout(() => URL.revokeObjectURL(url), 60000)
                          } catch (e: any) {
                            toast.error(e?.response?.status === 404 ? 'Pedido sem NF-e autorizada' : 'Erro ao baixar XML')
                          }
                        }}
                        className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-ink-50 hover:bg-ink-100 text-ink-700 text-sm font-medium">
                        <FileText size={16} /> Baixar XML
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-xs text-ink-500">Faturado <strong>sem nota</strong>. Se o cliente pedir, emita a NF-e agora — <strong>só o fiscal</strong>, sem repetir baixa de estoque nem a conta a receber (já lançados no faturamento).</p>
                      <button
                        onClick={() => { if (confirm('Gerar a NF-e fiscal deste pedido agora?\n\nEmite a nota na SEFAZ SEM repetir baixa de estoque ou conta a receber.')) gerarNota.mutate() }}
                        disabled={gerarNota.isPending}
                        className="w-full px-4 py-2.5 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-700 hover:from-indigo-600 hover:to-indigo-800 text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
                        {gerarNota.isPending ? <Loader2 className="animate-spin" size={16} /> : <FileText size={16} />}
                        {gerarNota.isPending ? 'Emitindo NF-e…' : 'Gerar nota fiscal (NF-e)'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* FEATURE 11/08: CANCELAR na SEFAZ. Aparece sempre que houver documento
                  autorizado — NF-e (55) ou NFC-e (65) —, inclusive em venda de PDV, que
                  antes não tinha ação nenhuma nesta tela. Cancela DE VERDADE: assina e
                  transmite o evento; se a SEFAZ recusar, nada é marcado no sistema. */}
              {emissaoAutorizada && (
                <div>
                  <div className="text-xs font-semibold text-ink-500 uppercase mb-2">
                    Cancelar {ehCupom ? 'cupom' : 'nota'} na SEFAZ
                  </div>
                  <p className="text-xs text-ink-500 mb-2">
                    {ehCupom ? 'Cupom' : 'Nota'} nº <b>{emissaoAutorizada.numero}</b>
                    {emissaoAutorizada.serie ? ` / série ${emissaoAutorizada.serie}` : ''} autorizad{ehCupom ? 'o' : 'a'}.
                    O prazo da SEFAZ é de <b>24h</b> após a autorização. Exige senha de admin ou gerente.
                  </p>
                  <button
                    onClick={pedirCancelamentoFiscal}
                    disabled={cancelarFiscal.isPending}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 text-sm font-medium disabled:opacity-50">
                    {cancelarFiscal.isPending ? <Loader2 className="animate-spin" size={16} /> : <XIcon size={16} />}
                    {cancelarFiscal.isPending
                      ? 'Cancelando na SEFAZ…'
                      : `Cancelar ${ehCupom ? 'cupom fiscal' : 'nota fiscal'}`}
                  </button>
                </div>
              )}

              {/* Boleto (forma=boleto, pedido faturado) */}
              {['faturado', 'entregue', 'expedido'].includes(pedido.status) && pedido.forma_pagamento === 'boleto' && (
                <div>
                  <div className="text-xs font-semibold text-ink-500 uppercase mb-2">Boleto</div>
                  <button onClick={verBoleto} disabled={boletoLoading}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-cyan-50 hover:bg-cyan-100 text-cyan-800 text-sm font-medium disabled:opacity-50">
                    {boletoLoading ? <Loader2 className="animate-spin" size={16} /> : <FileText size={16} />} Gerar / ver boleto
                  </button>
                </div>
              )}

              {/* FEATURE 30/06: Pagamento MODULAR — seção própria, independe do status OMS.
                  Confirmar pagamento é desacoplado de "Aprovar pedido": pode ser no começo,
                  meio ou fim. Pré-faturamento só marca a flag (sem caixa); ao faturar a
                  conta a receber nasce paga (reconcilia). */}
              {!isPDV && !['cancelado', 'devolvido'].includes(pedido.status) && (
                <div>
                  <div className="text-xs font-semibold text-ink-500 uppercase mb-2">Pagamento</div>
                  <div className="space-y-2">
                    {/* 30/06: seletor Começo/Meio/Fim REMOVIDO — só gravava etiqueta sem
                        efeito prático (a confirmação real é o botão abaixo). Endpoint
                        /momento-pagamento mantido no backend caso vire funcional depois. */}
                    {pedido.pagamento_confirmado_em ? (
                      <>
                        <div className="flex items-center gap-2 text-xs bg-emerald-50 text-emerald-800 rounded px-2 py-1.5">
                          <CheckCircle2 size={14} />
                          <span className="flex-1">Pagamento confirmado{pedido.pagamento_confirmado_em ? ` em ${new Date(pedido.pagamento_confirmado_em).toLocaleDateString('pt-BR')}` : ''}</span>
                        </div>
                        <BotaoAcao icon={Undo2} cor="bg-amber-600 hover:bg-amber-700"
                          label="Desfazer pagamento"
                          sublabel="Reverte a confirmação (e a baixa que ela fez)"
                          loading={desfazerPag.isPending}
                          onClick={() => { if (confirm('Desfazer a confirmação de pagamento deste pedido?')) desfazerPag.mutate(undefined) }} />
                      </>
                    ) : (
                      <BotaoAcao icon={Wallet} cor="bg-teal-600 hover:bg-teal-700"
                        label="Confirmar pagamento"
                        sublabel="Marca como pago — sem lançar no caixa"
                        loading={confirmarPag.isPending}
                        onClick={() => confirmarPag.mutate(undefined)} />
                    )}
                  </div>
                </div>
              )}

              {/* FEATURE 30/06: pipeline MODULAR — o usuário escolhe quais etapas este
                  pedido tem. Etapa desligada não aparece nas ações (o fluxo a pula).
                  Só editável enquanto cedo (aberto/aprovado). */}
              {!isPDV && !isSimplificado && ['aberto', 'aprovado'].includes(pedido.status) && (
                <div>
                  <div className="text-xs font-semibold text-ink-500 uppercase mb-2">Etapas deste pedido</div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {([
                      ['aprovacao', 'Aprovação'],
                      ['separacao', 'Separação'],
                      ['embalagem', 'Embalagem'],
                      ['expedicao', 'Expedição'],
                    ] as const).map(([key, lbl]) => {
                      const on = etapas[key]
                      // não dá pra desligar uma etapa já em curso/concluída
                      const travada = (key === 'aprovacao' && pedido.status !== 'aberto')
                      return (
                        <button key={key} type="button"
                          disabled={setEtapas.isPending || travada}
                          onClick={() => setEtapas.mutate({ [key]: !on })}
                          className={`px-2 py-1.5 rounded text-xs font-medium border flex items-center justify-center gap-1 transition-all disabled:opacity-50 ${
                            on ? 'bg-brand-600 text-white border-brand-600' : 'bg-white border-ink-200 text-ink-500 line-through'
                          }`}>
                          {on ? <CheckCircle2 size={12} /> : <XIcon size={12} />} {lbl}
                        </button>
                      )
                    })}
                  </div>
                  <div className="text-[10px] text-ink-400 mt-1">Desligue o que este pedido não usa (ex.: sem embalagem). Faturamento é sempre obrigatório.</div>
                </div>
              )}

              {/* Acoes contextuais */}
              {!isPDV && acoesDisponiveis.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-ink-500 uppercase mb-2">Próxima ação</div>
                  <div className="space-y-2">
                    {isSimplificado && ['aberto','aprovado','separacao','embalagem','aguardando_nf'].includes(pedido.status) && (
                      <div className="space-y-2">
                        <div className="text-[10px] text-ink-500 uppercase font-semibold px-1">Faturar venda</div>
                        <button
                          onClick={() => {
                            if (confirm('Faturar COM NF-e? Emite a NF-e na SEFAZ, baixa estoque e gera a receber.')) {
                              finalizarDireto.mutate({ emitir_nfe: true })
                            }
                          }}
                          disabled={finalizarDireto.isPending}
                          className="w-full px-4 py-3 rounded-lg bg-gradient-to-r from-emerald-500 to-emerald-700 hover:from-emerald-600 hover:to-emerald-800 text-white font-bold text-base flex items-center justify-center gap-2 shadow-md disabled:opacity-50">
                          {finalizarDireto.isPending ? <Loader2 className="animate-spin" size={18} /> : <CheckCircle2 size={18} />}
                          {finalizarDireto.isPending ? 'Faturando…' : 'Faturar com NF-e (fiscal)'}
                        </button>
                        <button
                          onClick={() => {
                            if (confirm('Faturar SEM nota (venda simples)? Baixa estoque e gera a receber, sem emitir NF-e.')) {
                              finalizarDireto.mutate({ emitir_nfe: false })
                            }
                          }}
                          disabled={finalizarDireto.isPending}
                          className="w-full px-4 py-2.5 rounded-lg border-2 border-ink-300 bg-white hover:bg-ink-50 text-ink-700 font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50">
                          <FileText size={16} /> Faturar sem nota (simples)
                        </button>
                      </div>
                    )}
                    {/* FEATURE 30/06: aprovar ≠ receber. Este botão só libera p/ separação;
                        o pagamento é confirmado na própria seção "Pagamento" (modular). */}
                    {!isSimplificado && acoesDisponiveis.includes('aprovar') && (
                      <BotaoAcao icon={CheckCircle2} cor="bg-emerald-600 hover:bg-emerald-700"
                        label="Aprovar pedido"
                        sublabel="Libera para separação (não recebe o pagamento)"
                        loading={aprovar.isPending}
                        onClick={() => aprovar.mutate({})} />
                    )}
                    {/* FEATURE 30/06: pipeline modular — avança pulando etapas desligadas. */}
                    {!isSimplificado && acoesDisponiveis.includes('avancar') && (
                      <BotaoAcao icon={ArrowRight} cor="bg-emerald-600 hover:bg-emerald-700"
                        label={proximaEtapa ? `Avançar → ${_STAGE_LABEL[proximaEtapa] || proximaEtapa}` : 'Avançar'}
                        sublabel="Pula as etapas desligadas neste pedido"
                        loading={avancar.isPending}
                        onClick={() => avancar.mutate({})} />
                    )}
                    {!isSimplificado && acoesDisponiveis.includes('reprovar') && (
                      <BotaoAcao icon={Ban} cor="bg-red-600 hover:bg-red-700"
                        label="Reprovar pagamento"
                        sublabel="Cancela o pedido"
                        loading={reprovar.isPending}
                        onClick={() => {
                          const m = prompt('Motivo da reprovação:')
                          if (m) reprovar.mutate(m)
                        }} />
                    )}
                    {!isSimplificado && acoesDisponiveis.includes('iniciar-sep') && (
                      <BotaoAcao icon={PackageCheck} cor="bg-indigo-600 hover:bg-indigo-700"
                        label="Iniciar separação"
                        loading={iniciarSep.isPending}
                        onClick={() => iniciarSep.mutate()} />
                    )}
                    {!isSimplificado && acoesDisponiveis.includes('pausar-sep') && (
                      <BotaoAcao icon={PauseCircle} cor="bg-amber-600 hover:bg-amber-700"
                        label="Pausar separação"
                        sublabel="Volta para 'aprovado' (ex: produto faltou)"
                        loading={pausarSep.isPending}
                        onClick={() => {
                          const obs = prompt('Motivo da pausa:') || ''
                          pausarSep.mutate(obs)
                        }} />
                    )}
                    {!isSimplificado && acoesDisponiveis.includes('concluir-sep') && (
                      <>
                        <BotaoAcao icon={Barcode} cor="bg-indigo-600 hover:bg-indigo-700"
                          label="Abrir tela de separação"
                          sublabel="Bipar produtos"
                          onClick={() => { nav(`/vendas/pedidos/${pedidoId}/separacao`); onClose() }} />
                        <BotaoAcao icon={ArrowRight} cor="bg-brand-600 hover:bg-brand-700"
                          label="Concluir separação"
                          sublabel="Vai para embalagem (se já estiver 100%)"
                          loading={concluirSep.isPending}
                          onClick={() => concluirSep.mutate()} />
                      </>
                    )}
                    {!isSimplificado && acoesDisponiveis.includes('abrir-embalagem') && (
                      <BotaoAcao icon={Package2} cor="bg-purple-600 hover:bg-purple-700"
                        label="Abrir tela de embalagem"
                        sublabel="Cadastrar volumes e imprimir etiquetas"
                        onClick={() => { nav(`/vendas/pedidos/${pedidoId}/embalagem`); onClose() }} />
                    )}
                                        {!isSimplificado && acoesDisponiveis.includes('concluir-emb') && (
                      <BotaoAcao icon={Package2} cor="bg-purple-600 hover:bg-purple-700"
                        label="Concluir embalagem"
                        sublabel="Fica aguardando emissão da NF"
                        loading={concluirEmb.isPending}
                        onClick={() => concluirEmb.mutate()} />
                    )}
                    {!isSimplificado && acoesDisponiveis.includes('emitir-nf') && (
                      <div className="space-y-1">
                        <div className="text-[10px] text-ink-500 uppercase font-semibold px-1">Faturar COM nota fiscal</div>
                        <div className="grid grid-cols-2 gap-2">
                          <button onClick={() => emitirNF.mutate('NFe')} disabled={emitirNF.isPending}
                            className="px-3 py-2.5 rounded-lg bg-gradient-to-br from-indigo-500 to-indigo-700 hover:from-indigo-600 hover:to-indigo-800 text-white text-sm font-medium flex flex-col items-center disabled:opacity-50">
                            <span>📄 NF-e</span>
                            <span className="text-[10px] opacity-80">modelo 55</span>
                          </button>
                          <button onClick={() => emitirNF.mutate('NFCe')} disabled={emitirNF.isPending}
                            className="px-3 py-2.5 rounded-lg bg-gradient-to-br from-violet-500 to-violet-700 hover:from-violet-600 hover:to-violet-800 text-white text-sm font-medium flex flex-col items-center disabled:opacity-50">
                            <span>🧾 NFC-e</span>
                            <span className="text-[10px] opacity-80">modelo 65</span>
                          </button>
                        </div>
                        <button
                          onClick={() => carregarPreview.mutate(pedidoId)}
                          disabled={carregarPreview.isPending}
                          className="w-full px-3 py-2 rounded-lg border border-dashed border-brand-400 text-brand-700 hover:bg-brand-50 text-xs font-medium disabled:opacity-50"
                          title="Gerar preview da NFe usando emissor proprio (sem transmitir pra SEFAZ)"
                        >
                          {carregarPreview.isPending ? 'Gerando preview...' : '🧪 Preview NFe propria + DANFE'}
                        </button>
                      </div>
                    )}
                    {!isSimplificado && acoesDisponiveis.includes('marcar-faturado') && (
                      <BotaoAcao icon={FileText} cor="bg-slate-600 hover:bg-slate-700"
                        label="Faturar SEM nota (simples)"
                        sublabel="Sem NF-e — baixa estoque e gera a receber"
                        loading={marcarFat.isPending}
                        onClick={() => { if (confirm('Faturar SEM nota fiscal (venda simples)? Baixa estoque e gera a receber, sem emitir NF-e.')) marcarFat.mutate({}) }} />
                    )}
                    {acoesDisponiveis.includes('expedir') && (
                      <FormExpedir onSubmit={(d) => expedir.mutate(d)} loading={expedir.isPending} />
                    )}
                    {acoesDisponiveis.includes('entregue') && (
                      <BotaoAcao icon={CheckCircle2} cor="bg-emerald-600 hover:bg-emerald-700"
                        label="Marcar como entregue"
                        loading={entregue.isPending}
                        onClick={() => entregue.mutate()} />
                    )}
                    {!isSimplificado && acoesDisponiveis.includes('reabrir-pag') && (
                      <BotaoAcao icon={RotateCcw} cor="bg-amber-600 hover:bg-amber-700"
                        label="Voltar para aberto"
                        sublabel="Cancela a aprovação"
                        loading={reabrirPag.isPending}
                        onClick={() => {
                          const m = prompt('Motivo da reabertura:') || ''
                          reabrirPag.mutate(m)
                        }} />
                    )}
                    {!isSimplificado && acoesDisponiveis.includes('reabrir-sep') && (
                      <BotaoAcao icon={RotateCcw} cor="bg-amber-600 hover:bg-amber-700"
                        label="Voltar para separação"
                        sublabel="Reabre a separação (ex: faltou item)"
                        loading={reabrirSep.isPending}
                        onClick={() => {
                          const m = prompt('Motivo:') || ''
                          reabrirSep.mutate(m)
                        }} />
                    )}
                    {!isSimplificado && acoesDisponiveis.includes('reabrir-emb') && (
                      <BotaoAcao icon={RotateCcw} cor="bg-amber-600 hover:bg-amber-700"
                        label="Voltar para embalagem"
                        sublabel="Para ajustar volumes/etiquetas"
                        loading={reabrirEmb.isPending}
                        onClick={() => {
                          const m = prompt('Motivo:') || ''
                          reabrirEmb.mutate(m)
                        }} />
                    )}
                                        {acoesDisponiveis.includes('cancelar') && (
                      <BotaoAcao icon={XCircle} cor="bg-red-600 hover:bg-red-700"
                        label="Cancelar pedido"
                        sublabel="Fica registrado — estorna estoque e contas"
                        onClick={() => setShowCancelar(true)} />
                    )}
                    {['aprovado', 'aberto', 'cancelado', 'rascunho'].includes(pedido.status) && !pedido.separacao_iniciada_em && !pedido.expedido_em && (
                      <BotaoAcao icon={Trash2} cor="bg-red-700 hover:bg-red-800"
                        label="Excluir pedido"
                        sublabel="Remove permanentemente — útil pra reimportar do ML"
                        loading={excluir.isPending}
                        onClick={() => pedirExclusao({ descricao: `o pedido #${pedido.numero}`, onConfirm: () => excluir.mutate() })} />
                    )}
                  </div>
                </div>
              )}

              {/* Cancelar / Devolver — quando o botão do fluxo não cobre (PDV, faturado,
                  expedido, entregue). FIX (09/09, pedido do cliente): liberado pra
                  QUALQUER operador logado, não só admin — o botão nem aparecia pro
                  pessoal de balcão. Detecta a NF e decide cancelar x devolver. */}
              {!['cancelado', 'devolvido'].includes(pedido.status)
                && !(!isPDV && acoesDisponiveis.includes('cancelar')) && (
                <div>
                  <div className="text-xs font-semibold text-ink-500 uppercase mb-2">Cancelamento / Devolução</div>
                  <BotaoAcao icon={Undo2} cor="bg-red-600 hover:bg-red-700"
                    label="Cancelar / Devolver venda"
                    sublabel="Detecta a NF: cancela (até 24h) ou abre devolução"
                    onClick={() => setShowCancelar(true)} />
                </div>
              )}

              {/* Editar pedido OMS — admin/gerente/financeiro, só em estado pré-fluxo (rascunho/aberto). */}
              {podeEditar && !isPDV && ['rascunho', 'aberto', 'aprovado', 'bloqueado'].includes(pedido.status) && (
                <div>
                  <div className="text-xs font-semibold text-ink-500 uppercase mb-2">Administração</div>
                  <BotaoAcao icon={Pencil} cor="bg-amber-600 hover:bg-amber-700"
                    label="Editar pedido"
                    sublabel="Cliente, itens, preços, desconto, forma e prazo"
                    onClick={() => setShowEditarOMS(true)} />
                </div>
              )}

              {/* Imprimir - disponivel em quase todos os status */}
              {!['aberto', 'cancelado', 'rascunho'].includes(pedido.status) && !isPDV && (
                <div>
                  <div className="text-xs font-semibold text-ink-500 uppercase mb-2">Imprimir</div>
                  <div className="space-y-2">
                    <button onClick={() => imprimirPDF(`/vendas/pedidos/${pedidoId}/imprimir-pedido`)}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm">
                      <FileText size={14} /> Imprimir pedido (A4)
                    </button>
                    {pedido.forma_pagamento === 'boleto' && (
                      <button onClick={async () => {
                        try {
                          const r = await api.get(`/financeiro/boletos/por-pedido/${pedidoId}`)
                          const parcelas = r.data.parcelas || []
                          if (!parcelas.length) { toast.error('Sem conta a receber — fature o pedido primeiro'); return }
                          let alvo = parcelas.find((p: any) => p.ja_gerado)
                          if (!alvo) {
                            await api.post('/financeiro/boletos/gerar', { conta_ids: parcelas.map((p: any) => p.conta_id), conta_bancaria_id: r.data.conta_bancaria_id })
                            alvo = parcelas[0]
                          }
                          imprimirPDF(`/financeiro/boletos/${alvo.conta_id}/pdf`)
                        } catch (e: any) { toast.error(e?.response?.data?.detail || 'Falha no boleto') }
                      }}
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm">
                        <FileText size={14} /> Imprimir boleto
                      </button>
                    )}
                    {etapas.separacao && ['separacao', 'embalagem', 'aguardando_nf', 'faturado'].includes(pedido.status) && (
                      <button onClick={() => imprimirPDF(`/vendas/pedidos/${pedidoId}/mapa-separacao-pdf`)}
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm">
                        🗺️ Mapa de separação
                      </button>
                    )}
                    <div className="grid grid-cols-2 gap-2">
                      <button onClick={() => imprimirPDF(`/vendas/pedidos/${pedidoId}/etiqueta-propria?formato=etiqueta`)}
                        className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-slate-700 hover:bg-slate-800 text-white text-sm">
                        📦 Etiqueta 10×15
                      </button>
                      <button onClick={() => imprimirPDF(`/vendas/pedidos/${pedidoId}/etiqueta-propria?formato=a4`)}
                        className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-slate-600 hover:bg-slate-700 text-white text-sm">
                        📄 Etiqueta A4
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Reimpressão do cupom/recibo — vendas do PDV (balcão). Sai igual ao
                  original: emissor da filial que vendeu, bike na troca como desconto,
                  formas de pagamento. Backend bloqueia venda cancelada. */}
              {pedido.status !== 'cancelado' && (
                <div>
                  <div className="text-xs font-semibold text-ink-500 uppercase mb-2">
                    {isPDV ? 'Imprimir / Enviar' : 'Enviar'}
                  </div>
                  <div className="space-y-2">
                    {isPDV && (
                      <button onClick={() => setShowCupom(true)}
                        className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm">
                        <Printer size={14} /> Reimprimir cupom / recibo
                      </button>
                    )}
                    <button onClick={() => enviarWhats.mutate()} disabled={enviarWhats.isPending}
                      className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm disabled:opacity-50">
                      {enviarWhats.isPending ? <Loader2 size={14} className="animate-spin" /> : <MessageCircle size={14} />}
                      {enviarWhats.isPending ? 'Enviando…' : 'Enviar comprovante no WhatsApp'}
                    </button>
                  </div>
                </div>
              )}

              {/* FEATURE 03/07: editar venda já feita (observação livre; cliente/vendedor c/ senha) */}
              <div>
                <div className="text-xs font-semibold text-ink-500 uppercase mb-2">Editar</div>
                <button onClick={() => setShowEditarVenda(true)}
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-ink-100 hover:bg-ink-200 text-ink-800 text-sm">
                  <Pencil size={14} /> Editar venda
                </button>
              </div>

                            {/* Timeline */}
              <div>
                <div className="text-xs font-semibold text-ink-500 uppercase mb-2">
                  Histórico ({eventos.length} eventos)
                </div>
                {eventos.length === 0 ? (
                  <div className="text-sm text-ink-400 text-center py-4 bg-ink-50 rounded">
                    {isPDV ? 'Pedido de PDV — sem fluxo' : 'Sem eventos registrados'}
                  </div>
                ) : (
                  <div className="border-l-2 border-ink-200 ml-2 pl-4 space-y-3">
                    {eventos.map((ev: any) => (
                      <div key={ev.id} className="relative">
                        <div className="absolute -left-[22px] w-3 h-3 rounded-full bg-brand-500 mt-1" />
                        <div className="text-sm">
                          <div className="font-medium capitalize">
                            {ev.tipo.replace(/_/g, ' ')}
                          </div>
                          <div className="text-xs text-ink-500">
                            {ev.status_anterior} → {ev.status_novo}
                          </div>
                          {ev.observacao && (
                            <div className="text-xs text-ink-600 italic mt-1">"{ev.observacao}"</div>
                          )}
                          {ev.dados && Object.keys(ev.dados).length > 0 && (
                            <div className="text-xs text-ink-500 mt-1">
                              {Object.entries(ev.dados).filter(([, v]) => v).map(([k, v]) =>
                                <div key={k}>{k}: <span className="font-mono">{String(v)}</span></div>,
                              )}
                            </div>
                          )}
                          <div className="text-[11px] text-ink-400 mt-0.5">
                            {new Date(ev.criado_em).toLocaleString('pt-BR')}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      {excecaoModal && (
        <ModalExcecaoCredito
          detalhe={excecaoModal.detalhe}
          isAdmin={isAdmin}
          onClose={() => setExcecaoModal(null)}
          onAutorizar={(just) => {
            const { retry } = excecaoModal
            setExcecaoModal(null)
            retry(true, just)
          }}
        />
      )}
      {showCancelar && pedido && (
        <ModalCancelarPedido
          pedidoId={pedidoId}
          numero={pedido.numero}
          clienteNome={mapCliente[pedido.cliente_id]}
          onClose={() => setShowCancelar(false)}
          onFeito={() => { setShowCancelar(false); invalidate() }}
        />
      )}
      {showEditarVenda && (
        <EditarVendaModal
          pedidoId={pedidoId}
          onClose={() => setShowEditarVenda(false)}
          onSaved={() => invalidate()}
        />
      )}

      {showEditarOMS && pedido && (
        <ModalEditarPedidoPDV
          oms
          pedidoId={pedidoId}
          mapCliente={mapCliente}
          onClose={() => setShowEditarOMS(false)}
          onSalvo={() => { setShowEditarOMS(false); invalidate() }}
        />
      )}
      {showCupom && pedido && (
        <PreviewCupomModal
          pedidoId={pedidoId}
          numero={pedido.numero}
          onClose={() => setShowCupom(false)}
        />
      )}
    </>
  )
}


// =============================================================
// BOTAO DE ACAO
// =============================================================
function BotaoAcao({ icon: Icon, cor, label, sublabel, loading, onClick }: {
  icon: any; cor: string; label: string; sublabel?: string
  loading?: boolean; onClick: () => void
}) {
  return (
    <button onClick={onClick} disabled={loading}
      className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-white text-sm font-medium
                  disabled:opacity-50 transition-colors ${cor}`}>
      {loading ? <Loader2 size={16} className="animate-spin" /> : <Icon size={16} />}
      <div className="text-left flex-1">
        <div>{label}</div>
        {sublabel && <div className="text-[11px] font-normal opacity-90">{sublabel}</div>}
      </div>
    </button>
  )
}


// =============================================================
// FORM EXPEDIR
// =============================================================
function FormExpedir({ onSubmit, loading }: {
  onSubmit: (d: { transportadora: string; rastreio_codigo: string }) => void
  loading: boolean
}) {
  const [transp, setTransp] = useState('')
  const [rastreio, setRastreio] = useState('')
  return (
    <div className="border border-teal-300 rounded-lg p-3 bg-teal-50/50 space-y-2">
      <div className="flex items-center gap-2 text-teal-800 text-sm font-medium">
        <Truck size={14} /> Expedir pedido
      </div>
      <div className="grid grid-cols-2 gap-2">
        <input className="input input-sm" placeholder="Transportadora"
          value={transp} onChange={e => setTransp(e.target.value)} />
        <input className="input input-sm" placeholder="Código rastreio"
          value={rastreio} onChange={e => setRastreio(e.target.value)} />
      </div>
      <button disabled={loading || !transp}
        onClick={() => onSubmit({ transportadora: transp, rastreio_codigo: rastreio })}
        className="w-full bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium py-2 rounded-lg disabled:opacity-50">
        {loading ? <><Loader2 size={14} className="inline animate-spin" /> Expedindo...</> : 'Confirmar expedição'}
      </button>
    </div>
  )
}


// =============================================================
// MODAL DE EXCECAO DE CREDITO (CRED-7b)
// =============================================================
function ModalExcecaoCredito({
  detalhe, isAdmin, onClose, onAutorizar,
}: {
  detalhe: any
  isAdmin: boolean
  onClose: () => void
  onAutorizar: (just: string) => void
}) {
  const [just, setJust] = useState('')
  const av = detalhe?.avaliacao || {}
  const cli = detalhe?.cliente || {}

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-[60]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="px-5 py-4 border-b">
          <h3 className="font-semibold text-red-700 flex items-center gap-2">
            <AlertTriangle size={18} /> Limite de crédito excedido
          </h3>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <p className="text-ink-700">
            {detalhe?.message || 'O cliente estourou o limite disponível.'}
          </p>
          <div className="bg-ink-50 border border-ink-200 rounded p-3 space-y-1 text-xs">
            <div className="font-semibold text-ink-700">{cli.nome} {cli.codigo && <span className="text-ink-500 font-mono">({cli.codigo})</span>}</div>
            {av.limite !== null && av.limite !== undefined && (
              <div className="flex justify-between"><span className="text-ink-500">Limite:</span> <b>{formatMoney(av.limite)}</b></div>
            )}
            <div className="flex justify-between"><span className="text-ink-500">Saldo devedor atual:</span> <b>{formatMoney(av.saldo_atual || 0)}</b></div>
            <div className="flex justify-between"><span className="text-ink-500">Disponível:</span> <b className={Number(av.disponivel) < 0 ? 'text-red-700' : ''}>{formatMoney(av.disponivel || 0)}</b></div>
            <div className="flex justify-between"><span className="text-ink-500">Valor do pedido:</span> <b>{formatMoney(av.valor_novo || 0)}</b></div>
          </div>

          {isAdmin ? (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
                Como admin, você pode autorizar exceção. A liberação será registrada no histórico de auditoria do cliente.
              </div>
              <div>
                <label className="label">Justificativa (mín. 10 chars)</label>
                <textarea className="input" rows={3}
                  value={just} onChange={e => setJust(e.target.value)}
                  placeholder="Ex: Cliente de confiança, regulariza até sexta" />
                <div className="text-xs text-ink-500 mt-1">{just.length}/1000</div>
              </div>
            </>
          ) : (
            <div className="bg-ink-50 border border-ink-200 rounded p-3 text-xs text-ink-700">
              Somente administradores podem autorizar exceção.
              <br />Entre em contato com um admin ou ajuste o limite em <b>Financeiro → Análise de Crédito</b>.
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t bg-ink-50 flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost text-sm">Cancelar</button>
          {isAdmin && (
            <button
              onClick={() => {
                if (just.trim().length < 10) {
                  toast.error('Justificativa precisa de no mínimo 10 caracteres')
                  return
                }
                onAutorizar(just.trim())
              }}
              className="btn-primary !bg-red-600 hover:!bg-red-700"
            >
              <AlertTriangle size={14} /> Autorizar exceção
            </button>
          )}
        </div>
      </div>
    </div>
  )
}


// =============================================================
// MODAL EDITAR PEDIDO (OMS — admin/gerente, pré-fluxo)
// =============================================================
function ModalEditarPedidoPDV({ pedidoId, onClose, onSalvo, oms = false, mapCliente }: {
  pedidoId: number
  onClose: () => void
  onSalvo: () => void
  oms?: boolean
  mapCliente?: Record<number, string>
}) {
  const [cliente, setCliente] = useState<any>(null)
  const [observacao, setObservacao] = useState('')
  const [descontoTotal, setDescontoTotal] = useState('0')
  const [itens, setItens] = useState<Array<{
    produto_id: number; descricao: string; quantidade: number;
    preco_unitario: number; desconto: number;
  }>>([])
  const [buscaCliente, setBuscaCliente] = useState('')
  const [openCliente, setOpenCliente] = useState(false)
  const [buscaProduto, setBuscaProduto] = useState('')
  const [openProduto, setOpenProduto] = useState(false)
  const [salvando, setSalvando] = useState(false)
  const [formaPagamento, setFormaPagamento] = useState('dinheiro')
  const [condicaoId, setCondicaoId] = useState<number | ''>('')
  const [tipoVendaEdit, setTipoVendaEdit] = useState('')

  const { data: venda, isLoading } = useQuery<any>({
    queryKey: ['venda-editar', oms, pedidoId],
    queryFn: async () => (await api.get(oms ? `/vendas/${pedidoId}` : `/pdv/venda/${pedidoId}`)).data,
  })

  useEffect(() => {
    if (!venda) return
    setObservacao(venda.observacao || '')
    if (oms) {
      setDescontoTotal(String(venda.desconto ?? 0))
      setFormaPagamento(venda.forma_pagamento || 'dinheiro')
      setCondicaoId(venda.condicao_pagamento_id || '')
      setTipoVendaEdit(venda.tipo_venda || '')
      setItens((venda.itens || []).map((it: any) => ({
        produto_id: it.produto_id || 0,
        descricao: it.descricao,
        quantidade: Number(it.quantidade),
        preco_unitario: Number(it.preco_unitario),
        desconto: Number(it.desconto) || 0,
      })))
      if (venda.cliente_id) {
        setCliente({
          id: venda.cliente_id,
          nome: mapCliente?.[venda.cliente_id] || venda.cliente_nome || `#${venda.cliente_id}`,
          documento: venda.cliente_documento,
        })
      }
    } else {
      setItens((venda.itens || []).map((it: any) => ({
        produto_id: it.produto_id || 0,
        descricao: it.descricao,
        quantidade: it.qtd,
        preco_unitario: it.preco,
        desconto: it.desconto || 0,
      })))
      if (venda.cliente_id) {
        setCliente({
          id: venda.cliente_id,
          nome: venda.cliente_nome,
          documento: venda.cliente_documento,
        })
      }
    }
  }, [venda])

  const { data: pessoasBusca = [] } = useQuery<any[]>({
    queryKey: ['editar-pdv-clientes', buscaCliente],
    queryFn: async () => (await api.get('/cadastros/pessoas', {
      params: { q: buscaCliente, tipo: 'cliente', limit: 10 },
    })).data,
    enabled: openCliente && buscaCliente.length >= 2,
  })

  const { data: produtosBusca = [] } = useQuery<any[]>({
    queryKey: ['editar-pdv-produtos', buscaProduto],
    queryFn: async () => (await api.get('/pdv/buscar', {
      params: { q: buscaProduto, limit: 10 },
    })).data,
    enabled: openProduto && buscaProduto.length >= 2,
  })

  const { data: formasCfg = [] } = useQuery<any[]>({
    queryKey: ['edit-formas'], enabled: oms,
    queryFn: async () => (await api.get('/caixa/config-formas')).data,
  })
  const { data: condicoesCfg = [] } = useQuery<any[]>({
    queryKey: ['edit-cond', formaPagamento], enabled: oms && !!formaPagamento,
    queryFn: async () => (await api.get('/caixa/condicoes-pagamento', { params: { forma: formaPagamento } })).data,
  })

  const subtotal = itens.reduce((s, i) => s + (i.quantidade * i.preco_unitario - i.desconto), 0)
  const desc = parseFloat(descontoTotal.replace(',', '.')) || 0
  const total = Math.max(0, subtotal - desc)

  function adicionarItem(p: any) {
    setItens(prev => {
      const existe = prev.find(i => i.produto_id === p.id)
      if (existe) {
        return prev.map(i => i.produto_id === p.id ? { ...i, quantidade: i.quantidade + 1 } : i)
      }
      return [...prev, {
        produto_id: p.id,
        descricao: p.descricao,
        quantidade: 1,
        preco_unitario: Number(p.preco_venda),
        desconto: 0,
      }]
    })
    setBuscaProduto('')
    setOpenProduto(false)
  }

  async function salvar() {
    if (itens.length === 0) { toast.error('Pedido precisa ter ao menos 1 item'); return }
    setSalvando(true)
    try {
      const itensPayload = itens.map(i => ({
        produto_id: i.produto_id,
        quantidade: i.quantidade,
        preco_unitario: i.preco_unitario,
        desconto: i.desconto,
      }))
      if (oms) {
        await api.put(`/vendas/pedidos/${pedidoId}/editar`, {
          cliente_id: cliente?.id || null,
          observacao: observacao || null,
          desconto: desc,
          forma_pagamento: formaPagamento,
          condicao_pagamento_id: condicaoId || null,
          tipo_venda: tipoVendaEdit || undefined,
          itens: itensPayload,
        })
      } else {
        await api.put(`/pdv/pedido/${pedidoId}/editar`, {
          cliente_id: cliente?.id || null,
          observacao: observacao || null,
          desconto_total: desc,
          itens: itensPayload,
        })
      }
      toast.success('Pedido atualizado')
      onSalvo()
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro ao salvar edição')
    } finally {
      setSalvando(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-[80] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-ink-200">
          <div>
            <h3 className="text-lg font-bold text-ink-900">
              ✏️ {oms ? 'Editar pedido' : 'Editar pedido PDV'} {venda?.numero ? `#${venda.numero}` : ''}
            </h3>
            <p className="text-[11px] text-amber-700 mt-0.5">
              {oms
                ? 'Altere cliente, itens, preços e desconto. Disponível em pedidos ainda não faturados.'
                : 'Reverte estoque e contas antigas, refaz com os novos valores. Pagamentos ajustam pra bater com o novo total.'}
            </p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-ink-100 text-ink-500"><XIcon size={18} /></button>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-ink-400 flex items-center justify-center gap-2">
            <Loader2 size={16} className="animate-spin" /> Carregando...
          </div>
        ) : (
          <div className="flex-1 overflow-auto p-5 space-y-4">
            {/* Cliente */}
            <div>
              <label className="text-[11px] text-ink-600 uppercase font-medium tracking-wide">Cliente</label>
              {cliente ? (
                <div className="flex items-center gap-2 px-3 py-2 mt-1 bg-ink-50 border border-ink-200 rounded-lg">
                  <span className="flex-1 text-sm">{cliente.nome}{cliente.documento ? ` · ${cliente.documento}` : ''}</span>
                  <button onClick={() => setCliente(null)} className="text-ink-500 hover:text-rose-600"><XIcon size={14} /></button>
                </div>
              ) : (
                <div className="relative">
                  <input value={buscaCliente} onChange={e => { setBuscaCliente(e.target.value); setOpenCliente(true) }}
                    onFocus={() => setOpenCliente(true)}
                    onBlur={() => setTimeout(() => setOpenCliente(false), 200)}
                    placeholder="Buscar nome, CPF ou CNPJ..."
                    className="w-full mt-1 px-3 py-2 text-sm bg-ink-50 border border-ink-200 rounded-lg focus:bg-white focus:border-brand-400 focus:outline-none" />
                  {openCliente && buscaCliente.length >= 2 && pessoasBusca.length > 0 && (
                    <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-ink-200 rounded-lg shadow-lg max-h-48 overflow-auto">
                      {pessoasBusca.map((p: any) => (
                        <button key={p.id} onMouseDown={e => e.preventDefault()}
                          onClick={() => { setCliente(p); setBuscaCliente(''); setOpenCliente(false) }}
                          className="w-full text-left px-3 py-2 text-xs hover:bg-brand-50 border-b last:border-0 border-ink-100">
                          <div className="font-medium">{p.nome}</div>
                          {p.documento && <div className="text-[10px] text-ink-500">{p.documento}</div>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Itens */}
            <div>
              <label className="text-[11px] text-ink-600 uppercase font-medium tracking-wide">Itens</label>
              <div className="mt-1 space-y-2">
                {itens.length === 0 && (
                  <div className="text-xs text-ink-400 italic px-3 py-2">Nenhum item — adicione abaixo</div>
                )}
                {itens.map((it, idx) => (
                  <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-ink-50 border border-ink-200 rounded-lg">
                    <span className="flex-1 text-sm font-medium truncate">{it.descricao}</span>
                    <input type="number" min="0" step="0.001" value={it.quantidade}
                      onChange={e => setItens(prev => prev.map((x, i) => i === idx ? { ...x, quantidade: parseFloat(e.target.value) || 0 } : x))}
                      onFocus={e => e.target.select()}
                      className="w-20 px-2 py-1 text-sm text-center bg-white border border-ink-200 rounded" />
                    <input type="number" min="0" step="0.01" value={it.preco_unitario}
                      onChange={e => setItens(prev => prev.map((x, i) => i === idx ? { ...x, preco_unitario: parseFloat(e.target.value) || 0 } : x))}
                      onFocus={e => e.target.select()}
                      className="w-24 px-2 py-1 text-sm text-right bg-white border border-ink-200 rounded" />
                    <input type="number" min="0" step="0.01" value={it.desconto}
                      onChange={e => setItens(prev => prev.map((x, i) => i === idx ? { ...x, desconto: parseFloat(e.target.value) || 0 } : x))}
                      onFocus={e => e.target.select()}
                      placeholder="Desc"
                      className="w-20 px-2 py-1 text-sm text-right bg-white border border-ink-200 rounded" />
                    <span className="w-24 text-right text-sm font-semibold">
                      {formatMoney(it.quantidade * it.preco_unitario - it.desconto)}
                    </span>
                    <button onClick={() => setItens(prev => prev.filter((_, i) => i !== idx))}
                      className="p-1 text-ink-400 hover:text-rose-600"><XIcon size={14} /></button>
                  </div>
                ))}
              </div>
              {/* Adicionar produto */}
              <div className="relative mt-2">
                <input value={buscaProduto} onChange={e => { setBuscaProduto(e.target.value); setOpenProduto(true) }}
                  onFocus={() => setOpenProduto(true)}
                  onBlur={() => setTimeout(() => setOpenProduto(false), 200)}
                  placeholder="+ Adicionar produto (busca por nome/código/EAN)..."
                  className="w-full px-3 py-2 text-sm bg-white border-2 border-dashed border-ink-300 rounded-lg focus:border-brand-400 focus:outline-none" />
                {openProduto && buscaProduto.length >= 2 && produtosBusca.length > 0 && (
                  <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-ink-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                    {produtosBusca.map((p: any) => (
                      <button key={p.id} onMouseDown={e => e.preventDefault()}
                        onClick={() => adicionarItem(p)}
                        className="w-full text-left px-3 py-2 text-xs hover:bg-brand-50 border-b last:border-0 border-ink-100 flex items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{p.descricao}</div>
                          <div className="text-[10px] text-ink-500">{p.codigo}{p.ean ? ` · ${p.ean}` : ''}</div>
                        </div>
                        <span className="text-sm font-bold text-brand-600">{formatMoney(p.preco_venda)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Desconto + Observação */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] text-ink-600 uppercase font-medium tracking-wide">Desconto total (R$)</label>
                <input value={descontoTotal} onChange={e => setDescontoTotal(e.target.value)}
                  type="number" min="0" step="0.01"
                  className="w-full mt-1 px-3 py-2 text-sm bg-ink-50 border border-ink-200 rounded-lg focus:bg-white focus:border-brand-400 focus:outline-none" />
              </div>
              <div>
                <label className="text-[11px] text-ink-600 uppercase font-medium tracking-wide">Observação</label>
                <input value={observacao} onChange={e => setObservacao(e.target.value)}
                  placeholder="Sai no cupom"
                  className="w-full mt-1 px-3 py-2 text-sm bg-ink-50 border border-ink-200 rounded-lg focus:bg-white focus:border-brand-400 focus:outline-none" />
              </div>
            </div>

            {/* Forma de pagamento + Prazo (só OMS) */}
            {oms && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] text-ink-600 uppercase font-medium tracking-wide">Forma de pagamento</label>
                  <select value={formaPagamento} onChange={e => { setFormaPagamento(e.target.value); setCondicaoId('') }}
                    className="w-full mt-1 px-3 py-2 text-sm bg-ink-50 border border-ink-200 rounded-lg focus:bg-white focus:border-brand-400 focus:outline-none">
                    {formasCfg.filter((f: any) => f.ativo).map((f: any) => <option key={f.forma} value={f.forma}>{f.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] text-ink-600 uppercase font-medium tracking-wide">Prazo / condição</label>
                  <select value={condicaoId} onChange={e => setCondicaoId(e.target.value ? Number(e.target.value) : '')}
                    disabled={condicoesCfg.length === 0}
                    className="w-full mt-1 px-3 py-2 text-sm bg-ink-50 border border-ink-200 rounded-lg focus:bg-white focus:border-brand-400 focus:outline-none disabled:opacity-50">
                    <option value="">{condicoesCfg.length ? '— à vista / sem prazo —' : 'sem prazo p/ esta forma'}</option>
                    {condicoesCfg.map((c: any) => <option key={c.id} value={c.id}>{c.nome} ({c.parcelas}x: {(c.dias || []).join('/')}d)</option>)}
                  </select>
                </div>
              </div>
            )}

            {oms && (
              <div>
                <label className="text-[11px] text-ink-600 uppercase font-medium tracking-wide">Tipo de venda</label>
                <select value={tipoVendaEdit} onChange={e => setTipoVendaEdit(e.target.value)}
                  className="w-full mt-1 px-3 py-2 text-sm bg-ink-50 border border-ink-200 rounded-lg focus:bg-white focus:border-brand-400 focus:outline-none">
                  <option value="">— não definido (decide no faturar) —</option>
                  <option value="fiscal">Com nota (NF-e)</option>
                  <option value="simples">Sem nota (simples)</option>
                  <option value="bonificacao">🎁 Bonificação (NF)</option>
                  <option value="bonificacao_simples">🎁 Bonificação simples (sem nota)</option>
                </select>
              </div>
            )}

            {/* Resumo */}
            <div className="bg-brand-50 border border-brand-200 rounded-lg p-3 grid grid-cols-3 gap-2 text-sm">
              <div>
                <div className="text-[10px] text-brand-700 uppercase">Subtotal</div>
                <div className="font-semibold">{formatMoney(subtotal)}</div>
              </div>
              <div>
                <div className="text-[10px] text-brand-700 uppercase">Desconto</div>
                <div className="font-semibold">-{formatMoney(desc)}</div>
              </div>
              <div>
                <div className="text-[10px] text-brand-700 uppercase">Novo total</div>
                <div className="font-bold text-lg text-brand-700">{formatMoney(total)}</div>
              </div>
            </div>

            {!oms && (
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                ⚠️ Os pagamentos atuais ({(venda?.pagamentos || []).length} linha{(venda?.pagamentos || []).length !== 1 ? 's' : ''})
                serão ajustados automaticamente pra bater com o novo total.
                Se o cliente mudou o que pagou, abra a venda na tela de Contas a Receber depois pra ajustar.
              </div>
            )}
          </div>
        )}

        <div className="px-5 py-3 border-t border-ink-200 grid grid-cols-2 gap-2">
          <button onClick={onClose} disabled={salvando}
            className="px-4 py-2.5 rounded-lg bg-ink-100 hover:bg-ink-200 text-ink-700 font-medium">
            Cancelar
          </button>
          <button onClick={salvar} disabled={salvando || itens.length === 0}
            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-semibold disabled:opacity-50">
            {salvando ? <Loader2 size={16} className="animate-spin" /> : '💾'} Salvar edição
          </button>
        </div>
      </div>
    </div>
  )
}


// =============================================================
// MODAL CANCELAR / DEVOLVER PEDIDO (fiscal-aware)
// =============================================================
function ModalCancelarPedido({ pedidoId, numero, clienteNome, onClose, onFeito }: {
  pedidoId: number; numero: number | string; clienteNome?: string;
  onClose: () => void; onFeito: () => void;
}) {
  const [motivo, setMotivo] = useState('')
  const [destino, setDestino] = useState<'credito' | 'abater'>('credito')
  // FIX (09/09, pedido do cliente): exige senha de admin/gerente/financeiro
  const [senha, setSenha] = useState('')

  const { data: info, isLoading } = useQuery({
    queryKey: ['cancelamento-info', pedidoId],
    queryFn: async () => (await api.get(`/vendas/pedidos/${pedidoId}/cancelamento-info`)).data,
  })

  const cancelarMut = useMutation({
    mutationFn: (cancelar_nf: boolean) =>
      api.post(`/vendas/pedidos/${pedidoId}/cancelar`, { motivo, cancelar_nf, senha }),
    onSuccess: (r: any) => {
      toast.success(r?.data?.nf_cancelada ? 'Venda e NF-e canceladas — estoque e contas estornados'
                                          : 'Venda cancelada — estoque e contas estornados')
      onFeito()
    },
    onError: (e: any) => toast.error(extrairMsg(e)),
  })

  const devolucaoMut = useMutation({
    mutationFn: () => api.post(`/vendas/pedidos/${pedidoId}/devolucao`, { motivo, destino_financeiro: destino, senha }),
    onSuccess: (r: any) => {
      const cred = r?.data?.credito_gerado
      toast.success(cred > 0
        ? `Devolução feita — crédito de ${formatMoney(cred)} pro cliente`
        : 'Devolução feita — nota de entrada gerada e financeiro estornado')
      onFeito()
    },
    onError: (e: any) => toast.error(extrairMsg(e)),
  })

  function extrairMsg(e: any): string {
    const d = e?.response?.data?.detail
    if (typeof d === 'string') return d
    if (d?.mensagem) return d.mensagem
    return 'Erro ao processar'
  }

  const acao: string = info?.acao || ''
  const busy = cancelarMut.isPending || devolucaoMut.isPending
  const motivoOk = motivo.trim().length >= 5

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="font-semibold text-ink-900">Cancelar / Devolver #{numero}</h3>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700"><XIcon size={20} /></button>
        </div>

        {isLoading ? (
          <div className="p-8 flex justify-center"><Loader2 className="animate-spin text-ink-400" /></div>
        ) : acao === 'nenhuma' ? (
          <div className="p-6 text-sm text-ink-600">
            Pedido já está <b>{info?.status}</b>. Nada a fazer.
          </div>
        ) : (
          <div className="p-5 space-y-4">
            {acao === 'cancelar' && (
              <div className="rounded-lg bg-blue-50 text-blue-800 text-sm p-3 flex gap-2">
                <FileText size={18} className="shrink-0 mt-0.5" />
                <span><b>Venda sem nota fiscal.</b> Cancela direto: estorna estoque e contas a receber. Sem nada de fiscal.</span>
              </div>
            )}
            {acao === 'cancelar_com_nf' && (
              <div className="rounded-lg bg-amber-50 text-amber-800 text-sm p-3 flex gap-2">
                <AlertTriangle size={18} className="shrink-0 mt-0.5" />
                <span>
                  Tem <b>NF-e {info?.nf?.numero}</b> autorizada há {info?.horas_desde_emissao}h
                  (dentro das 24h, faltam ~{info?.horas_restantes}h).
                  Cancelar a venda <b>cancela a nota fiscal</b> na SEFAZ também, e estorna estoque/contas.
                </span>
              </div>
            )}
            {acao === 'devolucao' && (
              <div className="rounded-lg bg-rose-50 text-rose-800 text-sm p-3 flex gap-2">
                <Undo2 size={18} className="shrink-0 mt-0.5" />
                <span>
                  <b>NF-e {info?.nf?.numero}</b> foi emitida há mais de 24h — não dá pra cancelar a nota.
                  Vou fazer a <b>devolução</b>: gera <b>nota de entrada</b>, devolve o estoque e estorna o financeiro.
                </span>
              </div>
            )}

            {acao === 'devolucao' && (
              <div>
                <div className="text-xs font-semibold text-ink-500 uppercase mb-2">Valor já pago pelo cliente</div>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => setDestino('credito')}
                    className={`flex flex-col items-start gap-1 p-3 rounded-lg border text-left text-sm ${destino === 'credito' ? 'border-brand-600 bg-brand-50 ring-1 ring-brand-600' : 'border-ink-200'}`}>
                    <Wallet size={18} className="text-brand-600" />
                    <span className="font-medium">Gerar crédito</span>
                    <span className="text-xs text-ink-500">Vira haver do cliente pra usar em outra compra</span>
                  </button>
                  <button onClick={() => setDestino('abater')}
                    className={`flex flex-col items-start gap-1 p-3 rounded-lg border text-left text-sm ${destino === 'abater' ? 'border-brand-600 bg-brand-50 ring-1 ring-brand-600' : 'border-ink-200'}`}>
                    <RotateCcw size={18} className="text-brand-600" />
                    <span className="font-medium">Restituir</span>
                    <span className="text-xs text-ink-500">Gera conta a pagar pra devolver o dinheiro</span>
                  </button>
                </div>
                {clienteNome && destino === 'credito' && (
                  <p className="text-xs text-ink-500 mt-2">O crédito fica no nome de <b>{clienteNome}</b>.</p>
                )}
              </div>
            )}

            <div>
              <label className="text-xs font-semibold text-ink-500 uppercase">Motivo</label>
              <textarea value={motivo} onChange={e => setMotivo(e.target.value)} rows={2}
                placeholder="Ex.: cliente desistiu / produto com defeito"
                className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm" />
              {acao === 'cancelar_com_nf' && (
                <p className="text-[11px] text-ink-400 mt-1">A SEFAZ exige justificativa de 15+ caracteres — completo automaticamente se faltar.</p>
              )}
            </div>

            {/* FIX (09/09, pedido do cliente): senha de admin/gerente/financeiro
                pra liberar o cancelamento/devolução (mesma trava do estorno de caixa) */}
            <div>
              <label className="text-xs font-semibold text-ink-500 uppercase">Senha de autorização</label>
              <input type="password" value={senha} onChange={e => setSenha(e.target.value)}
                autoComplete="new-password"
                placeholder="Senha de admin, gerente ou financeiro"
                className="mt-1 w-full rounded-lg border border-ink-200 px-3 py-2 text-sm" />
            </div>

            <div className="flex gap-2 pt-1">
              <button onClick={onClose} disabled={busy}
                className="flex-1 px-4 py-2.5 rounded-lg border border-ink-200 text-sm font-medium text-ink-600 hover:bg-ink-50">
                Voltar
              </button>
              {acao === 'devolucao' ? (
                <button onClick={() => devolucaoMut.mutate()} disabled={busy || !motivoOk || !senha}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-sm font-medium flex items-center justify-center gap-2">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <Undo2 size={16} />}
                  Fazer devolução
                </button>
              ) : (
                <button onClick={() => cancelarMut.mutate(acao === 'cancelar_com_nf')} disabled={busy || !motivoOk || !senha}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium flex items-center justify-center gap-2">
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <XCircle size={16} />}
                  {acao === 'cancelar_com_nf' ? 'Cancelar venda + NF' : 'Cancelar venda'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
